// SBIS Consulting — Balance Sheet + Inventory izveštaji
import { state } from './state.js';
import { sb } from './supabase.js';
import { fNum, fNum2, pNum } from './utils.js';
import { toast, showLoading, hideLoading } from './ui.js';
import XLSX from 'xlsx-js-style';

let lastBsData = [];
let lastBsKurs = 108;
let lastBsMonth = 0;
let lastBsYear = 0;

const MONTHS_SR = ['Januar','Februar','Mart','April','Maj','Jun','Jul','Avgust','Septembar','Oktobar','Novembar','Decembar'];

// Class groupings for BS display
const BS_CLASSES = [
  { prefix: '0', label: 'UPISANI KAPITAL I STALNA IMOVINA' },
  { prefix: '1', label: 'ZALIHE I SREDSTVA NAMENJENA PRODAJI' },
  { prefix: '2', label: 'KRATKOROČNA POTRAŽIVANJA, PLASMANI I GOTOVINA' },
  { prefix: '3', label: 'KAPITAL' },
  { prefix: '4', label: 'OBAVEZE' },
  { prefix: '5', label: 'RASHODI' },
  { prefix: '6', label: 'PRIHODI' },
  { prefix: '7', label: 'OTVARANJE I ZAKLJUČAK' }
];

// ============================================================
// ZVANIČNI MESEČNI BALANCE SHEET (engleske kategorije) — mapiranje konto→linija,
// validirano na maju 2026 (reprodukuje BS 12Months u cent). Vidi [[fintrack-monthly-report]].
// Koristi SAMO sintetičke kontoe (r.synthetic) da ne duplo broji analitike.
// ============================================================
function bsReportLine(k) {
  k = String(k || '');
  // AKTIVA
  if (/^24/.test(k)) return 'Cash & Banks';
  if (/^204/.test(k)) return 'Account Receivables';
  if (/^20/.test(k)) return 'Account Receivables Interco.';
  if (/^1/.test(k)) return 'Inventory';                      // klasa 1 (zalihe + dati avansi 15x)
  if (/^(22|27|28)/.test(k)) return 'Other current assets';   // PDV pretporez, depoziti, AVR
  // STALNA / NEMATERIJALNA
  if (/^012/.test(k)) return 'Plant Machinery & Equipment';   // softver → oprema (izbor knjigovođe)
  if (/^01/.test(k)) return 'Intangible Assets';
  if (/^(020|021|022)/.test(k)) return 'Land, Plot, Buildings & Improvements';
  if (/^023/.test(k)) return 'Plant Machinery & Equipment';
  if (/^027/.test(k)) return 'Buildings & Construction in progress';
  if (/^029/.test(k)) return 'Advances for PP&E';
  if (/^02/.test(k)) return 'Land, Plot, Buildings & Improvements';
  if (/^03/.test(k)) return 'Biological agents';
  // OBAVEZE
  if (/^435/.test(k)) return 'Trade Payables - domestic';
  if (/^436/.test(k)) return 'Trade Payables - foreign';
  if (/^(45|46)/.test(k)) return 'Short term payables (Salaries)';
  if (/^(43|47|48|49)/.test(k)) return 'Other liabilities';
  // KAPITAL
  if (/^(00|30|31)/.test(k)) return 'Capital';
  if (/^(34|35)/.test(k)) return 'Previous period gain/loss';
  return null;                                                // klasa 5,6 → rezultat; 7,8,9 → van BS
}

// Vrati strukturirani zvanični BS: niz {label, usd, type:'head'|'line'|'sub'}
export function buildOfficialBS(data, kurs) {
  const k = (kurs && kurs > 0) ? kurs : 1;
  const sum = {}; let plNet = 0;
  for (const r of data) {
    if (r.synthetic === false) continue;                      // preskoči analitike (zbir je na sintetici)
    const konto = String(r.konto || '');
    const saldo = parseFloat(r.saldo || 0);
    if (/^[56]/.test(konto)) { plNet += saldo; continue; }     // klasa 5/6 → rezultat perioda
    const L = bsReportLine(konto);
    if (!L) continue;
    sum[L] = (sum[L] || 0) + saldo;
  }
  const u = L => (sum[L] || 0) / k;                            // aktiva: saldo/kurs
  const uA = L => Math.abs(sum[L] || 0) / k;                   // pasiva/kapital: prikaži pozitivno
  const curResult = -plNet / k;                                // prihodi − rashodi
  const assetLines = ['Cash & Banks', 'Account Receivables', 'Account Receivables Interco.', 'Inventory', 'Other current assets'];
  const fixedLines = ['Buildings & Construction in progress', 'Plant Machinery & Equipment', 'Land, Plot, Buildings & Improvements', 'Advances for PP&E', 'Biological agents'];
  const liabLines = ['Trade Payables - domestic', 'Trade Payables - foreign', 'Short term payables (Salaries)', 'Other liabilities'];
  const ca = assetLines.reduce((s, L) => s + u(L), 0);
  const fa = fixedLines.reduce((s, L) => s + u(L), 0);
  const intang = u('Intangible Assets');
  const totalAssets = ca + fa + intang;
  const cl = liabLines.reduce((s, L) => s + uA(L), 0);
  const totalEquity = totalAssets - cl;                        // bilansna ravnoteža (kao u zvaničnom izveštaju)
  const rows = [];
  rows.push({ label: 'ASSETS', type: 'head' });
  assetLines.forEach(L => rows.push({ label: L, usd: u(L), type: 'line' }));
  rows.push({ label: 'CURRENT ASSETS', usd: ca, type: 'sub' });
  fixedLines.forEach(L => rows.push({ label: L, usd: u(L), type: 'line' }));
  rows.push({ label: 'FIXED ASSETS', usd: fa, type: 'sub' });
  rows.push({ label: 'Intangible Assets', usd: intang, type: 'line' });
  rows.push({ label: 'TOTAL ASSETS', usd: totalAssets, type: 'sub' });
  rows.push({ label: 'LIABILITIES', type: 'head' });
  liabLines.forEach(L => rows.push({ label: L, usd: uA(L), type: 'line' }));
  rows.push({ label: 'CURRENT LIABILITIES', usd: cl, type: 'sub' });
  rows.push({ label: 'TOTAL LIABILITIES', usd: cl, type: 'sub' });
  rows.push({ label: 'EQUITY', type: 'head' });
  rows.push({ label: 'Capital', usd: uA('Capital'), type: 'line' });
  rows.push({ label: 'Previous period gain/loss', usd: uA('Previous period gain/loss'), type: 'line' });
  rows.push({ label: 'Gain/Loss Current Period', usd: curResult, type: 'line' });
  rows.push({ label: 'TOTAL EQUITY', usd: totalEquity, type: 'sub' });
  rows.push({ label: 'TOTAL LIABILITIES + EQUITY', usd: cl + totalEquity, type: 'sub' });
  return rows;
}

function renderOfficialBS(out, data, kurs) {
  const rows = buildOfficialBS(data, kurs);
  const card = document.createElement('div'); card.className = 'card'; card.style.cssText = 'padding:0;margin-bottom:18px;overflow:hidden';
  const h = document.createElement('div'); h.style.cssText = 'padding:12px 18px;background:var(--surface);font-weight:700;font-size:18px;color:var(--acc)'; h.textContent = 'Balance Sheet — USD (zvanični format)'; card.appendChild(h);
  const tbl = document.createElement('table'); tbl.style.cssText = 'width:100%;border-collapse:collapse;font-size:16px';
  for (const r of rows) {
    const tr = document.createElement('tr');
    if (r.type === 'head') {
      const td = document.createElement('td'); td.colSpan = 2; td.style.cssText = 'padding:10px 18px;font-weight:700;font-size:13px;letter-spacing:1px;color:var(--tx3);text-transform:uppercase;background:var(--raised)'; td.textContent = r.label; tr.appendChild(td);
    } else {
      const sub = r.type === 'sub';
      const td1 = document.createElement('td'); td1.style.cssText = 'padding:' + (sub ? '11px 18px;font-weight:700;border-top:1px solid var(--border)' : '8px 18px 8px 32px') + ';color:var(--tx)'; td1.textContent = r.label;
      const td2 = document.createElement('td'); td2.style.cssText = 'padding:' + (sub ? '11px 18px;font-weight:700;border-top:1px solid var(--border)' : '8px 18px') + ';text-align:right;font-family:var(--mn);color:' + ((r.usd || 0) < -0.005 ? 'var(--dng)' : 'var(--tx)'); td2.textContent = fNum2(r.usd || 0);
      tr.appendChild(td1); tr.appendChild(td2);
    }
    tbl.appendChild(tr);
  }
  card.appendChild(tbl); out.appendChild(card);
}


// ============================================================
// ZVANIČNI MESEČNI P&L (engleske linije) — klasa 5 (rashodi) / 6 (prihodi).
// Mapiranje = col10 tagovi iz P&L EXPENSES + zaključivanje po nazivu (Revenue, Direct Labor,
// Raw Materials, D&A nisu tagovani u col10). NAPOMENA: iznosi su za PERIOD uvezenog Zaključnog
// lista (za mesečni P&L uvezi mesečni promet). Vidi [[fintrack-monthly-report]].
// ============================================================
function plReportLine(k) {
  k = String(k || '');
  // PRIHODI
  if (/^61/.test(k)) return 'Revenue';
  if (/^6/.test(k)) return 'Other income';
  // COGS
  if (/^511/.test(k)) return 'Raw Materials Costs';
  if (/^5300/.test(k)) return 'Manufacturing costs';
  if (/^(520|521)/.test(k)) return 'Direct Labor Costs';
  if (/^5131/.test(k)) return 'Energy Costs';
  if (/^(5120|5121|51260|532|5396)/.test(k)) return 'Maintenance Costs';
  // OPEX (specifično pre opšteg)
  if (/^55094/.test(k)) return 'Quality Control';
  if (/^(55092|55093)/.test(k)) return 'Environmental Protection';
  if (/^(550|5361)/.test(k)) return 'Administration & HR';
  if (/^552/.test(k)) return 'Insurance Costs';
  if (/^535/.test(k)) return 'Marketing & Supply Chain';
  if (/^(5132|5251|5252|5253|5290|5292|5310|5315|5591)/.test(k)) return 'Transportation & Logistics Costs';
  // OSTALO
  if (/^540/.test(k)) return 'Depreciation and amortization';
  if (/^56/.test(k)) return 'Net interest expense';
  if (/^5/.test(k)) return 'Other SG&A';
  return null;
}

export function buildOfficialPL(data, kurs) {
  const k = (kurs && kurs > 0) ? kurs : 1;
  const sum = {};
  for (const r of data) {
    if (r.synthetic === false) continue;
    const konto = String(r.konto || '');
    if (!/^[56]/.test(konto)) continue;
    const L = plReportLine(konto); if (!L) continue;
    sum[L] = (sum[L] || 0) + parseFloat(r.saldo || 0);
  }
  const v = L => Math.abs(sum[L] || 0) / k;                  // prihodi(−) i rashodi(+) → pozitivno
  const revenue = v('Revenue'), otherInc = v('Other income');
  const totalRev = revenue + otherInc;
  const cogsLines = ['Raw Materials Costs', 'Manufacturing costs', 'Direct Labor Costs', 'Energy Costs', 'Maintenance Costs', 'Warehousing Costs'];
  const cogs = cogsLines.reduce((s, L) => s + v(L), 0);
  const grossProfit = totalRev - cogs;
  const opexLines = ['Administration & HR', 'Marketing & Supply Chain', 'Quality Control', 'R&D', 'Environmental Protection', 'Other SG&A', 'Transportation & Logistics Costs', 'Insurance Costs'];
  const opex = opexLines.reduce((s, L) => s + v(L), 0);
  const ebitda = grossProfit - opex;
  const da = v('Depreciation and amortization');
  const ebit = ebitda - da;
  const interest = v('Net interest expense');
  const netIncome = ebit - interest;
  const rows = [];
  rows.push({ label: 'Revenue', usd: revenue, type: 'line' });
  rows.push({ label: 'Other income', usd: otherInc, type: 'line' });
  rows.push({ label: 'Total revenue', usd: totalRev, type: 'sub' });
  rows.push({ label: 'COST OF GOODS SOLD', type: 'head' });
  cogsLines.forEach(L => rows.push({ label: L, usd: v(L), type: 'line' }));
  rows.push({ label: 'Cost of goods sold', usd: cogs, type: 'sub' });
  rows.push({ label: 'Gross profit', usd: grossProfit, type: 'sub' });
  rows.push({ label: 'OPERATING EXPENSES', type: 'head' });
  opexLines.forEach(L => rows.push({ label: L, usd: v(L), type: 'line' }));
  rows.push({ label: 'Total operating expenses', usd: opex, type: 'sub' });
  rows.push({ label: 'EBITDA', usd: ebitda, type: 'sub' });
  rows.push({ label: 'Depreciation and amortization', usd: da, type: 'line' });
  rows.push({ label: 'EBIT', usd: ebit, type: 'sub' });
  rows.push({ label: 'Net interest expense', usd: interest, type: 'line' });
  rows.push({ label: 'Net Income', usd: netIncome, type: 'sub' });
  return rows;
}

// Generički render zvanične dvokolone (label | USD) — koristi i BS i P&L
function renderOfficialTable(out, title, rows) {
  const card = document.createElement('div'); card.className = 'card'; card.style.cssText = 'padding:0;margin-bottom:18px;overflow:hidden';
  const h = document.createElement('div'); h.style.cssText = 'padding:12px 18px;background:var(--surface);font-weight:700;font-size:18px;color:var(--acc)'; h.textContent = title; card.appendChild(h);
  const tbl = document.createElement('table'); tbl.style.cssText = 'width:100%;border-collapse:collapse;font-size:16px';
  for (const r of rows) {
    const tr = document.createElement('tr');
    if (r.type === 'head') {
      const td = document.createElement('td'); td.colSpan = 2; td.style.cssText = 'padding:10px 18px;font-weight:700;font-size:13px;letter-spacing:1px;color:var(--tx3);text-transform:uppercase;background:var(--raised)'; td.textContent = r.label; tr.appendChild(td);
    } else {
      const sub = r.type === 'sub';
      const td1 = document.createElement('td'); td1.style.cssText = 'padding:' + (sub ? '11px 18px;font-weight:700;border-top:1px solid var(--border)' : '8px 18px 8px 32px') + ';color:var(--tx)'; td1.textContent = r.label;
      const td2 = document.createElement('td'); td2.style.cssText = 'padding:' + (sub ? '11px 18px;font-weight:700;border-top:1px solid var(--border)' : '8px 18px') + ';text-align:right;font-family:var(--mn);color:' + ((r.usd || 0) < -0.005 ? 'var(--dng)' : 'var(--tx)'); td2.textContent = fNum2(r.usd || 0);
      tr.appendChild(td1); tr.appendChild(td2);
    }
    tbl.appendChild(tr);
  }
  card.appendChild(tbl); out.appendChild(card);
}

function renderOfficialPL(out, data, kurs) {
  renderOfficialTable(out, 'P&L — USD (zvanični format)', buildOfficialPL(data, kurs));
}

export async function generateBS(mode) {
  if (!state.CU) return;
  const selId = { bs: 'bsMonth', inv: 'invMonth', pl: 'plMonth' }[mode] || 'bsMonth';
  const yearId = { bs: 'bsYear', inv: 'invYear', pl: 'plYear' }[mode] || 'bsYear';
  const kursId = { bs: 'bsKurs', inv: 'invKurs', pl: 'plKurs' }[mode] || 'bsKurs';
  const month = parseInt(document.getElementById(selId).value);
  const year = parseInt(document.getElementById(yearId).value);
  const kurs = parseFloat(document.getElementById(kursId).value) || 108;

  showLoading();
  try {
    let data;
    if (state.mesecniGL && state.mesecniGL.length) {
      // Učitan Excel knjigovodstvo — koristi ga umesto baze (saldo_usd po trenutnom kursu)
      data = state.mesecniGL.map(r => ({ ...r, saldo_usd: (parseFloat(r.saldo || 0)) / kurs }));
    } else {
      const res = await sb.rpc('calculate_balance_sheet', { p_user_id: state.CU.id, p_year: year, p_month: month, p_kurs: kurs });
      if (res.error) throw res.error;
      data = res.data || [];
    }
    lastBsData = data || [];
    lastBsKurs = kurs;
    lastBsMonth = month;
    lastBsYear = year;
    if (mode === 'inv') renderInventory(lastBsData, kurs, month, year);
    else if (mode === 'pl') renderPL(lastBsData, kurs, month, year);
    else renderBS(lastBsData, kurs, month, year);
    const labels = { bs: 'Balance Sheet', inv: 'Inventory', pl: 'P&L' };
    toast((labels[mode] || 'Izveštaj') + ' generisan za ' + MONTHS_SR[month - 1] + ' ' + year);
  } catch (e) { toast('Greška: ' + (e.message || e), 'err'); }
  hideLoading();
}

function renderBS(data, kurs, month, year) {
  const out = document.getElementById('bsOutput'); out.textContent = '';
  if (!data.length) { out.textContent = 'Nema podataka.'; return; }

  document.getElementById('bsExpBtn').style.display = '';

  // Summary KPI
  const totalD = data.reduce((s, r) => s + parseFloat(r.duguje || 0), 0);
  const totalC = data.reduce((s, r) => s + parseFloat(r.potrazuje || 0), 0);
  const bar = document.createElement('div'); bar.style.cssText = 'display:flex;gap:14px;margin-bottom:16px;flex-wrap:wrap';
  [{ l: 'Konta', v: data.length, c: 'var(--acc)' }, { l: 'Ukupno Duguje', v: fNum(totalD), c: 'var(--dng)' }, { l: 'Ukupno Potražuje', v: fNum(totalC), c: 'var(--suc)' }, { l: 'Kurs', v: fNum(kurs), c: 'var(--tx)' }].forEach(s => {
    const card = document.createElement('div'); card.className = 'card'; card.style.cssText = 'padding:14px 22px;flex:1;min-width:180px';
    const lbl = document.createElement('div'); lbl.style.cssText = 'font-size:14px;color:var(--tx3);text-transform:uppercase;letter-spacing:.5px;margin-bottom:4px'; lbl.textContent = s.l;
    const val = document.createElement('div'); val.style.cssText = 'font-size:26px;font-weight:700;font-family:var(--mn);color:' + s.c; val.textContent = s.v;
    card.appendChild(lbl); card.appendChild(val); bar.appendChild(card);
  });
  out.appendChild(bar);

  // Zvanični mesečni BS (engleske kategorije, USD) — glavni prikaz
  renderOfficialBS(out, data, kurs);

  // Detaljni pregled po srpskim klasama (drill-down ispod)
  BS_CLASSES.forEach(cls => {
    const items = data.filter(r => r.konto.startsWith(cls.prefix));
    if (!items.length) return;
    const classD = items.reduce((s, r) => s + parseFloat(r.duguje || 0), 0);
    const classC = items.reduce((s, r) => s + parseFloat(r.potrazuje || 0), 0);
    const classSaldo = classD - classC;

    const card = document.createElement('div'); card.className = 'card'; card.style.cssText = 'padding:0;margin-bottom:12px;overflow:hidden';
    // Header as table matching column widths
    const hdr = document.createElement('div'); hdr.style.cssText = 'cursor:pointer;background:var(--surface)';
    const ht = document.createElement('table'); ht.style.cssText = 'width:100%;table-layout:fixed;border-collapse:collapse';
    const hcg = document.createElement('colgroup');
    [{w:'7%'},{w:'25%'},{w:'15%'},{w:'15%'},{w:'17%'},{w:'21%'}].forEach(c => { const col = document.createElement('col'); col.style.width = c.w; hcg.appendChild(col); });
    ht.appendChild(hcg);
    const htr2 = document.createElement('tr');
    const htd1 = document.createElement('td'); htd1.colSpan = 2; htd1.style.cssText = 'padding:10px 16px;font-weight:700;font-size:17px;color:var(--acc)'; htd1.textContent = 'Klasa ' + cls.prefix + ' \u2014 ' + cls.label;
    const htd3 = document.createElement('td'); htd3.style.cssText = 'padding:10px 16px;text-align:right;font-family:var(--mn);font-size:17px;font-weight:700;color:var(--dng);border-right:1px solid var(--border-lt)'; htd3.textContent = fNum(classD);
    const htd4 = document.createElement('td'); htd4.style.cssText = 'padding:10px 16px;text-align:right;font-family:var(--mn);font-size:17px;font-weight:700;color:var(--suc);border-right:1px solid var(--border-lt)'; htd4.textContent = fNum(classC);
    const htd5 = document.createElement('td'); htd5.style.cssText = 'padding:10px 16px;text-align:right;font-family:var(--mn);font-size:17px;font-weight:700;color:' + (classSaldo >= 0 ? 'var(--dng)' : 'var(--suc)'); htd5.textContent = fNum(classSaldo);
    const htd6 = document.createElement('td'); htd6.style.cssText = 'padding:10px 8px;text-align:center';
    const ch = document.createElement('span'); ch.style.cssText = 'color:var(--tx3);font-size:13px;transition:transform .2s'; ch.textContent = '\u25BC';
    htd6.appendChild(ch);
    htr2.appendChild(htd1); htr2.appendChild(htd3); htr2.appendChild(htd4); htr2.appendChild(htd5); htr2.appendChild(htd6);
    ht.appendChild(htr2); hdr.appendChild(ht);

    const body = document.createElement('div'); body.style.display = 'none';
    hdr.onclick = () => { const o = body.style.display !== 'none'; body.style.display = o ? 'none' : 'block'; ch.style.transform = o ? '' : 'rotate(180deg)'; };

    // Column headers
    const tbl = document.createElement('table'); tbl.style.cssText = 'width:100%;font-size:17px;border-collapse:collapse';
    // Column widths
    const cg = document.createElement('colgroup');
    [{w:'7%'},{w:'25%'},{w:'15%'},{w:'15%'},{w:'17%'},{w:'21%'}].forEach(c => { const col = document.createElement('col'); col.style.width = c.w; cg.appendChild(col); });
    tbl.appendChild(cg);
    const thead = document.createElement('thead'); const htr = document.createElement('tr'); htr.style.cssText = 'background:var(--raised);border-bottom:2px solid var(--border)';
    ['Šifra','Naziv','Duguje','Potražuje','Saldo','Saldo USD'].forEach((h,i) => {
      const th = document.createElement('th'); th.textContent = h;
      th.style.cssText = 'padding:12px 16px;font-size:14px;color:var(--tx3);text-transform:uppercase;letter-spacing:.5px;text-align:' + (i >= 2 ? 'right' : 'left');
      htr.appendChild(th);
    });
    thead.appendChild(htr); tbl.appendChild(thead);

    items.sort((a, b) => a.konto.localeCompare(b.konto)).forEach(r => {
      const d = parseFloat(r.duguje || 0), c = parseFloat(r.potrazuje || 0), saldo = parseFloat(r.saldo || 0);
      const tr = document.createElement('tr'); tr.style.borderBottom = '1px solid var(--border-lt)';
      const td1 = document.createElement('td'); td1.style.cssText = 'padding:10px 14px;font-family:var(--mn);font-weight:700;color:var(--acc);font-size:17px'; td1.textContent = r.konto;
      const td2 = document.createElement('td'); td2.style.cssText = 'padding:10px 14px;color:var(--tx);font-size:16px'; td2.textContent = r.naziv || '';
      const td3 = document.createElement('td'); td3.style.cssText = 'padding:10px 14px;text-align:right;font-family:var(--mn);font-size:17px;font-weight:600'; if (d > 0) { td3.textContent = fNum(d); td3.style.color = 'var(--dng)'; }
      const td4 = document.createElement('td'); td4.style.cssText = 'padding:10px 14px;text-align:right;font-family:var(--mn);font-size:17px;font-weight:600'; if (c > 0) { td4.textContent = fNum(c); td4.style.color = 'var(--suc)'; }
      const td5 = document.createElement('td'); td5.style.cssText = 'padding:10px 14px;text-align:right;font-family:var(--mn);font-weight:700;font-size:17px;color:' + (saldo >= 0 ? 'var(--dng)' : 'var(--suc)'); td5.textContent = fNum(saldo);
      const td6 = document.createElement('td'); td6.style.cssText = 'padding:10px 14px;text-align:right;font-family:var(--mn);font-size:17px;color:var(--tx3)'; td6.textContent = fNum(parseFloat(r.saldo_usd || 0));
      tr.appendChild(td1); tr.appendChild(td2); tr.appendChild(td3); tr.appendChild(td4); tr.appendChild(td5); tr.appendChild(td6);
      tbl.appendChild(tr);
    });
    // Class total row
    const ttr = document.createElement('tr'); ttr.style.cssText = 'background:var(--surface);font-weight:700;border-top:2px solid var(--acc)';
    const tt1 = document.createElement('td'); tt1.colSpan = 2; tt1.style.cssText = 'padding:12px 14px;font-size:17px;color:var(--acc)'; tt1.textContent = 'UKUPNO Klasa ' + cls.prefix;
    const tt3 = document.createElement('td'); tt3.style.cssText = 'padding:12px 14px;text-align:right;font-family:var(--mn);font-size:19px;color:var(--dng)'; tt3.textContent = fNum(classD);
    const tt4 = document.createElement('td'); tt4.style.cssText = 'padding:12px 14px;text-align:right;font-family:var(--mn);font-size:19px;color:var(--suc)'; tt4.textContent = fNum(classC);
    const tt5 = document.createElement('td'); tt5.style.cssText = 'padding:12px 14px;text-align:right;font-family:var(--mn);font-size:19px;font-weight:700;color:' + (classSaldo >= 0 ? 'var(--dng)' : 'var(--suc)'); tt5.textContent = fNum(classSaldo);
    const tt6 = document.createElement('td'); tt6.style.cssText = 'padding:12px 14px;text-align:right;font-family:var(--mn);font-size:19px;color:var(--tx3)'; tt6.textContent = fNum(classSaldo / kurs);
    ttr.appendChild(tt1); ttr.appendChild(tt3); ttr.appendChild(tt4); ttr.appendChild(tt5); ttr.appendChild(tt6);
    tbl.appendChild(ttr);
    body.appendChild(tbl); card.appendChild(hdr); card.appendChild(body); out.appendChild(card);
  });
}

function renderInventory(data, kurs, month, year) {
  const out = document.getElementById('invOutput'); out.textContent = '';
  const invData = data.filter(r => r.konto.startsWith('1'));
  if (!invData.length) { out.textContent = 'Nema podataka za zalihe.'; return; }

  document.getElementById('invExpBtn').style.display = '';

  const totalSaldo = invData.reduce((s, r) => s + parseFloat(r.saldo || 0), 0);

  // Summary
  const sumDiv = document.createElement('div'); sumDiv.className = 'card'; sumDiv.style.cssText = 'padding:10px 14px;margin-bottom:16px;display:flex;justify-content:space-between;align-items:center';
  const sumL = document.createElement('div'); sumL.style.cssText = 'font-size:16px;color:var(--tx2)'; sumL.textContent = 'Ukupne zalihe \u2014 ' + MONTHS_SR[month - 1] + ' ' + year;
  const sumR = document.createElement('div'); sumR.style.cssText = 'font-family:var(--mn);font-size:26px;font-weight:700;color:var(--acc)'; sumR.textContent = fNum(totalSaldo) + ' RSD';
  const sumU = document.createElement('div'); sumU.style.cssText = 'font-family:var(--mn);font-size:17px;color:var(--tx3);margin-top:2px'; sumU.textContent = fNum(totalSaldo / kurs) + ' USD';
  const sumRW = document.createElement('div'); sumRW.style.textAlign = 'right'; sumRW.appendChild(sumR); sumRW.appendChild(sumU);
  sumDiv.appendChild(sumL); sumDiv.appendChild(sumRW); out.appendChild(sumDiv);

  // Table
  const card = document.createElement('div'); card.className = 'card'; card.style.cssText = 'padding:0;overflow:hidden';
  const tbl = document.createElement('table'); tbl.style.cssText = 'width:100%;font-size:17px;border-collapse:collapse';
  const cgI = document.createElement('colgroup');
  [{w:'10%'},{w:'50%'},{w:'20%'},{w:'20%'}].forEach(c => { const col = document.createElement('col'); col.style.width = c.w; cgI.appendChild(col); });
  tbl.appendChild(cgI);
  const thead = document.createElement('thead'); const htr = document.createElement('tr'); htr.style.cssText = 'background:var(--raised)';
  ['Konto', 'Naziv', 'Saldo RSD', 'Saldo USD'].forEach(h => {
    const th = document.createElement('th'); th.textContent = h;
    th.style.cssText = 'text-align:left;padding:12px 16px;border-bottom:2px solid var(--border);color:var(--tx3);font-size:13px;text-transform:uppercase;letter-spacing:.5px';
    if (h.includes('Saldo')) th.style.textAlign = 'right';
    htr.appendChild(th);
  });
  thead.appendChild(htr); tbl.appendChild(thead);

  const tbody = document.createElement('tbody');
  invData.sort((a, b) => a.konto.localeCompare(b.konto)).forEach(r => {
    const saldo = parseFloat(r.saldo || 0);
    if (Math.abs(saldo) < 0.01) return;
    const tr = document.createElement('tr'); tr.style.borderBottom = '1px solid var(--border-lt)';
    const td1 = document.createElement('td'); td1.style.cssText = 'padding:10px 14px;font-family:var(--mn);font-weight:700;color:var(--acc);font-size:17px'; td1.textContent = r.konto;
    const td2 = document.createElement('td'); td2.style.cssText = 'padding:10px 14px;color:var(--tx);font-size:16px'; td2.textContent = r.naziv || '';
    const td3 = document.createElement('td'); td3.style.cssText = 'padding:10px 14px;text-align:right;font-family:var(--mn);font-weight:700;font-size:17px;color:' + (saldo >= 0 ? 'var(--dng)' : 'var(--suc)'); td3.textContent = fNum(saldo);
    const td4 = document.createElement('td'); td4.style.cssText = 'padding:10px 14px;text-align:right;font-family:var(--mn);font-size:17px;color:var(--tx3)'; td4.textContent = fNum(saldo / kurs);
    tr.appendChild(td1); tr.appendChild(td2); tr.appendChild(td3); tr.appendChild(td4); tbody.appendChild(tr);
  });
  // Total
  const ttr = document.createElement('tr'); ttr.style.cssText = 'background:var(--surface);font-weight:700;border-top:2px solid var(--border)';
  const tt1 = document.createElement('td'); tt1.colSpan = 2; tt1.style.cssText = 'padding:12px 14px;font-size:17px'; tt1.textContent = 'UKUPNO ZALIHE';
  const tt3 = document.createElement('td'); tt3.style.cssText = 'padding:12px 14px;text-align:right;font-family:var(--mn);font-size:19px;color:var(--acc)'; tt3.textContent = fNum(totalSaldo);
  const tt4 = document.createElement('td'); tt4.style.cssText = 'padding:12px 14px;text-align:right;font-family:var(--mn);font-size:19px;color:var(--tx3)'; tt4.textContent = fNum(totalSaldo / kurs);
  ttr.appendChild(tt1); ttr.appendChild(tt3); ttr.appendChild(tt4); tbody.appendChild(ttr);
  tbl.appendChild(tbody); card.appendChild(tbl); out.appendChild(card);
}

export function exportBsXlsx() { exportSheet(lastBsData, lastBsKurs, lastBsMonth, lastBsYear, 'bs'); }
export function exportInvXlsx() { exportSheet(lastBsData.filter(r => r.konto.startsWith('1')), lastBsKurs, lastBsMonth, lastBsYear, 'inv'); }
export function exportPlXlsx() { exportSheet(lastBsData.filter(r => r.konto.startsWith('5') || r.konto.startsWith('6')), lastBsKurs, lastBsMonth, lastBsYear, 'pl'); }

// ============================================================
// P&L RENDERING
// ============================================================
const PL_SECTIONS = [
  { title: 'PRIHODI (Revenue)', groups: [
    { prefix: '60', label: 'Prihodi od prodaje proizvoda i usluga' },
    { prefix: '61', label: 'Prihodi od prodaje robe' },
    { prefix: '62', label: 'Prihodi od aktiviranja učinaka' },
    { prefix: '64', label: 'Prihodi od premija, subvencija, dotacija' },
    { prefix: '65', label: 'Finansijski prihodi' },
    { prefix: '66', label: 'Ostali prihodi' },
    { prefix: '67', label: 'Prihodi od usklađivanja vrednosti' },
    { prefix: '68', label: 'Prihodi od korigovanja' }
  ], sign: 1, color: 'var(--suc)' },
  { title: 'RASHODI (Expenses)', groups: [
    { prefix: '50', label: 'Nabavna vrednost prodate robe' },
    { prefix: '51', label: 'Troškovi materijala' },
    { prefix: '52', label: 'Troškovi zarada i naknada' },
    { prefix: '53', label: 'Troškovi amortizacije' },
    { prefix: '54', label: 'Ostali poslovni rashodi' },
    { prefix: '55', label: 'Finansijski rashodi' },
    { prefix: '56', label: 'Ostali rashodi' },
    { prefix: '57', label: 'Rashodi od usklađivanja vrednosti' },
    { prefix: '58', label: 'Rashodi od korigovanja' }
  ], sign: -1, color: 'var(--dng)' }
];

function renderPL(data, kurs, month, year) {
  const out = document.getElementById('plOutput'); out.textContent = '';
  const plData = data.filter(r => r.konto.startsWith('5') || r.konto.startsWith('6'));
  if (!plData.length) { out.textContent = 'Nema podataka za P&L.'; return; }

  document.getElementById('plExpBtn').style.display = '';

  // Zvanični P&L (engleske linije, USD) — glavni prikaz; detaljni po klasama ostaje ispod
  renderOfficialPL(out, data, kurs);

  let totalRevenue = 0, totalExpenses = 0;

  const plCols = [{w:'8%'},{w:'32%'},{w:'30%'},{w:'30%'}];
  function mkCg() { const cg = document.createElement('colgroup'); plCols.forEach(c => { const col = document.createElement('col'); col.style.width = c.w; cg.appendChild(col); }); return cg; }

  PL_SECTIONS.forEach(sec => {
    let sectionTotal = 0;
    const secCard = document.createElement('div'); secCard.className = 'card'; secCard.style.cssText = 'padding:0;margin-bottom:12px;overflow:hidden';

    const secHdr = document.createElement('div'); secHdr.style.cssText = 'padding:10px 14px;background:var(--surface);border-bottom:2px solid ' + sec.color;
    const secTitle = document.createElement('h3'); secTitle.style.cssText = 'font-size:19px;color:' + sec.color + ';margin:0'; secTitle.textContent = sec.title;
    secHdr.appendChild(secTitle); secCard.appendChild(secHdr);

    sec.groups.forEach(grp => {
      const items = plData.filter(r => r.konto.startsWith(grp.prefix));
      if (!items.length) return;
      const grpTotal = items.reduce((s, r) => s + parseFloat(r.saldo || 0), 0);
      sectionTotal += grpTotal;

      // Group header as table
      const ght = document.createElement('table'); ght.style.cssText = 'width:100%;table-layout:fixed;border-collapse:collapse;cursor:pointer;background:var(--raised);border-bottom:1px solid var(--border-lt)';
      ght.appendChild(mkCg());
      const ghtr = document.createElement('tr');
      const ght1 = document.createElement('td'); ght1.colSpan = 2; ght1.style.cssText = 'padding:12px 16px;font-size:17px;font-weight:600;color:var(--tx)'; ght1.textContent = grp.prefix + ' \u2014 ' + grp.label;
      const ght3 = document.createElement('td'); ght3.style.cssText = 'padding:12px 16px;text-align:right;font-family:var(--mn);font-size:17px;font-weight:700;color:' + sec.color; ght3.textContent = fNum(Math.abs(grpTotal));
      const ght4 = document.createElement('td'); ght4.style.cssText = 'padding:12px 16px;text-align:right;font-family:var(--mn);font-size:17px;color:var(--tx3)';
      ght4.textContent = fNum(Math.abs(grpTotal) / kurs) + ' USD ';
      const chS = document.createElement('span'); chS.style.cssText = 'color:var(--tx3);font-size:14px;transition:transform .2s'; chS.textContent = '\u25BC';
      ght4.appendChild(chS);
      ghtr.appendChild(ght1); ghtr.appendChild(ght3); ghtr.appendChild(ght4); ght.appendChild(ghtr);

      const body = document.createElement('div'); body.style.display = 'none';
      ght.onclick = () => { const o = body.style.display !== 'none'; body.style.display = o ? 'none' : 'block'; chS.style.transform = o ? '' : 'rotate(180deg)'; };

      // Detail table
      const tbl = document.createElement('table'); tbl.style.cssText = 'width:100%;table-layout:fixed;border-collapse:collapse';
      tbl.appendChild(mkCg());
      const thdr = document.createElement('tr'); thdr.style.cssText = 'background:var(--raised);border-bottom:1px solid var(--border)';
      ['Šifra','Naziv','Iznos RSD','Iznos USD'].forEach((h,i) => { const th = document.createElement('th'); th.textContent = h; th.style.cssText = 'padding:8px 16px;font-size:14px;color:var(--tx3);text-transform:uppercase;text-align:' + (i >= 2 ? 'right' : 'left'); thdr.appendChild(th); });
      tbl.appendChild(thdr);
      items.sort((a, b) => a.konto.localeCompare(b.konto)).forEach(r => {
        const saldo = parseFloat(r.saldo || 0);
        const tr = document.createElement('tr'); tr.style.borderBottom = '1px solid var(--border-lt)';
        const td1 = document.createElement('td'); td1.style.cssText = 'padding:10px 16px;font-family:var(--mn);font-weight:700;color:var(--acc);font-size:17px'; td1.textContent = r.konto;
        const td2 = document.createElement('td'); td2.style.cssText = 'padding:10px 16px;color:var(--tx);font-size:16px'; td2.textContent = r.naziv || '';
        const td3 = document.createElement('td'); td3.style.cssText = 'padding:10px 16px;text-align:right;font-family:var(--mn);font-weight:600;font-size:17px;color:' + sec.color; td3.textContent = fNum(Math.abs(saldo));
        const td4 = document.createElement('td'); td4.style.cssText = 'padding:10px 16px;text-align:right;font-family:var(--mn);font-size:16px;color:var(--tx3)'; td4.textContent = fNum(Math.abs(saldo) / kurs);
        tr.appendChild(td1); tr.appendChild(td2); tr.appendChild(td3); tr.appendChild(td4); tbl.appendChild(tr);
      });
      body.appendChild(tbl); secCard.appendChild(ght); secCard.appendChild(body);
    });

    // Section total as table
    const stt = document.createElement('table'); stt.style.cssText = 'width:100%;table-layout:fixed;border-collapse:collapse;background:var(--surface);border-top:2px solid var(--border)';
    stt.appendChild(mkCg());
    const sttr = document.createElement('tr');
    const st1 = document.createElement('td'); st1.colSpan = 2; st1.style.cssText = 'padding:10px 16px;font-size:17px;font-weight:700;color:var(--tx)'; st1.textContent = 'UKUPNO ' + sec.title.split(' ')[0];
    const st3 = document.createElement('td'); st3.style.cssText = 'padding:10px 16px;text-align:right;font-family:var(--mn);font-size:19px;font-weight:700;color:' + sec.color; st3.textContent = fNum(Math.abs(sectionTotal)) + ' RSD';
    const st4 = document.createElement('td'); st4.style.cssText = 'padding:10px 16px;text-align:right;font-family:var(--mn);font-size:17px;color:var(--tx3)'; st4.textContent = fNum(Math.abs(sectionTotal) / kurs) + ' USD';
    sttr.appendChild(st1); sttr.appendChild(st3); sttr.appendChild(st4); stt.appendChild(sttr);
    secCard.appendChild(stt); out.appendChild(secCard);

    if (sec.sign === 1) totalRevenue = Math.abs(sectionTotal);
    else totalExpenses = Math.abs(sectionTotal);
  });

  // Net result as table
  const netProfit = totalRevenue - totalExpenses;
  const netCard = document.createElement('div'); netCard.className = 'card'; netCard.style.cssText = 'padding:0;overflow:hidden;border:2px solid ' + (netProfit >= 0 ? 'var(--suc)' : 'var(--dng)');
  const ntt = document.createElement('table'); ntt.style.cssText = 'width:100%;table-layout:fixed;border-collapse:collapse';
  ntt.appendChild(mkCg());
  const nttr = document.createElement('tr');
  const nt1 = document.createElement('td'); nt1.colSpan = 2; nt1.style.cssText = 'padding:14px 16px';
  const netTitle = document.createElement('div'); netTitle.style.cssText = 'font-size:19px;font-weight:700;color:var(--tx)'; netTitle.textContent = netProfit >= 0 ? 'NETO DOBIT' : 'NETO GUBITAK';
  const netSub = document.createElement('div'); netSub.style.cssText = 'font-size:13px;color:var(--tx3);margin-top:4px';
  netSub.textContent = 'Prihodi: ' + fNum(totalRevenue) + ' \u2014 Rashodi: ' + fNum(totalExpenses);
  nt1.appendChild(netTitle); nt1.appendChild(netSub);
  const nt3 = document.createElement('td'); nt3.style.cssText = 'padding:14px 16px;text-align:right;font-family:var(--mn);font-size:22px;font-weight:700;color:' + (netProfit >= 0 ? 'var(--suc)' : 'var(--dng)'); nt3.textContent = fNum(Math.abs(netProfit)) + ' RSD';
  const nt4 = document.createElement('td'); nt4.style.cssText = 'padding:14px 16px;text-align:right;font-family:var(--mn);font-size:17px;color:var(--tx3)'; nt4.textContent = fNum(Math.abs(netProfit) / kurs) + ' USD';
  nttr.appendChild(nt1); nttr.appendChild(nt3); nttr.appendChild(nt4); ntt.appendChild(nttr);
  netCard.appendChild(ntt); out.appendChild(netCard);
}

function exportSheet(data, kurs, month, year, mode) {
  if (!data.length) { toast('Generišite izveštaj prvo', 'err'); return; }
  const hS = { font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 11 }, fill: { fgColor: { rgb: '0F1923' } } };
  const nS = { font: { sz: 10 }, numFmt: '#,##0.000' };
  const tS = { font: { bold: true, sz: 11 }, fill: { fgColor: { rgb: 'FEF08A' } }, numFmt: '#,##0.000' };
  const secS = { font: { bold: true, sz: 11, color: { rgb: 'D4A843' } }, fill: { fgColor: { rgb: '1A2332' } } };
  function c(v, s) { return { v, s, t: typeof v === 'number' ? 'n' : 's' }; }
  const title = mode === 'inv' ? 'Inventory' : mode === 'pl' ? 'P&L' : 'Balance Sheet';
  const rows = [];
  rows.push([c('ECOR SERBIA — ' + title + ' ' + MONTHS_SR[month - 1] + ' ' + year, { font: { bold: true, sz: 14 } }), c('', {}), c('', {}), c('', {}), c('', {}), c('', {})]);
  rows.push([c('Kurs: ' + fNum(kurs), { font: { sz: 10 } }), c('', {}), c('', {}), c('', {}), c('', {}), c('', {})]);

  if (mode === 'pl') {
    // P&L export: grouped by Revenue/Expenses with abs values, matching screen
    rows.push(['Šifra', 'Naziv', 'Iznos RSD', 'Iznos USD'].map(h => c(h, hS)));
    const revData = data.filter(r => r.konto.startsWith('6')).sort((a, b) => a.konto.localeCompare(b.konto));
    const expData = data.filter(r => r.konto.startsWith('5')).sort((a, b) => a.konto.localeCompare(b.konto));
    let totalRev = 0, totalExp = 0;
    rows.push([c('PRIHODI (Revenue)', secS), c('', secS), c('', secS), c('', secS)]);
    revData.forEach(r => {
      const s = Math.abs(parseFloat(r.saldo || 0));
      totalRev += s;
      rows.push([c(r.konto, {}), c(r.naziv || '', {}), c(s, nS), c(s / kurs, nS)]);
    });
    rows.push([c('UKUPNO PRIHODI', tS), c('', tS), c(totalRev, tS), c(totalRev / kurs, tS)]);
    rows.push([c('', {}), c('', {}), c('', {}), c('', {})]);
    rows.push([c('RASHODI (Expenses)', secS), c('', secS), c('', secS), c('', secS)]);
    expData.forEach(r => {
      const s = Math.abs(parseFloat(r.saldo || 0));
      totalExp += s;
      rows.push([c(r.konto, {}), c(r.naziv || '', {}), c(s, nS), c(s / kurs, nS)]);
    });
    rows.push([c('UKUPNO RASHODI', tS), c('', tS), c(totalExp, tS), c(totalExp / kurs, tS)]);
    rows.push([c('', {}), c('', {}), c('', {}), c('', {})]);
    const net = totalRev - totalExp;
    rows.push([c(net >= 0 ? 'NETO DOBIT' : 'NETO GUBITAK', tS), c('', tS), c(Math.abs(net), tS), c(Math.abs(net) / kurs, tS)]);
    const ws = XLSX.utils.aoa_to_sheet(rows.map(r => r.map(c => c.v)));
    ws['!cols'] = [{ wch: 12 }, { wch: 40 }, { wch: 16 }, { wch: 14 }];
    rows.forEach((row, ri) => { row.forEach((cell, ci) => { const addr = XLSX.utils.encode_cell({ r: ri, c: ci }); if (ws[addr]) ws[addr].s = cell.s; }); });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, title);
    XLSX.writeFile(wb, 'pl_' + MONTHS_SR[month - 1] + '_' + year + '.xlsx', { bookSST: false, cellStyles: true });
    toast('Excel eksportovan');
  if (state.CU) sb.rpc('log_audit', { p_user_id: state.CU.id, p_action: 'export_excel', p_entity_type: 'mesecni', p_entity_name: 'excel_export' }).then(null, e => console.error('SBIS:', e));
    return;
  }

  // BS / Inventory export: standard format
  rows.push(['Šifra', 'Naziv', 'Duguje', 'Potražuje', 'Saldo', 'Saldo USD'].map(h => c(h, hS)));
  data.sort((a, b) => a.konto.localeCompare(b.konto)).forEach(r => {
    const d = parseFloat(r.duguje || 0), cr = parseFloat(r.potrazuje || 0), s = parseFloat(r.saldo || 0);
    if (mode === 'inv' && Math.abs(s) < 0.01) return;
    rows.push([c(r.konto, {}), c(r.naziv || '', {}), c(d || null, nS), c(cr || null, nS), c(s, nS), c(s / kurs, nS)]);
  });
  const totalD = data.reduce((s, r) => s + parseFloat(r.duguje || 0), 0);
  const totalC = data.reduce((s, r) => s + parseFloat(r.potrazuje || 0), 0);
  const totalS = totalD - totalC;
  rows.push([c('UKUPNO', tS), c('', tS), c(totalD, tS), c(totalC, tS), c(totalS, tS), c(totalS / kurs, tS)]);

  const ws = XLSX.utils.aoa_to_sheet(rows.map(r => r.map(c => c.v)));
  ws['!cols'] = [{ wch: 12 }, { wch: 40 }, { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 14 }];
  rows.forEach((row, ri) => { row.forEach((cell, ci) => { const addr = XLSX.utils.encode_cell({ r: ri, c: ci }); if (ws[addr]) ws[addr].s = cell.s; }); });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, title);
  XLSX.writeFile(wb, title.toLowerCase().replace(' ', '_') + '_' + MONTHS_SR[month - 1] + '_' + year + '.xlsx', { bookSST: false, cellStyles: true });
  toast('Excel eksportovan');
  if (state.CU) sb.rpc('log_audit', { p_user_id: state.CU.id, p_action: 'export_excel', p_entity_type: 'mesecni', p_entity_name: 'excel_export' }).then(null, e => console.error('SBIS:', e));
}

export function initBsSelectors(prefix) {
  const yearSel = document.getElementById(prefix + 'Year');
  if (!yearSel || yearSel.dataset.init === 'done') return;
  yearSel.dataset.init = 'done';
  const curYear = new Date().getFullYear();
  for (let y = curYear; y >= curYear - 3; y--) {
    const o = document.createElement('option'); o.value = y; o.textContent = y; yearSel.appendChild(o);
  }
}

// ============================================================
// MESEČNI EXCEL IMPORT — knjigovodstvene brojke (bruto bilans / GL)
// Oblik isti kao calculate_balance_sheet: [{konto,naziv,duguje,potrazuje,saldo}]
// Kad je učitan, BS/P&L/Inventory koriste njega umesto baze.
// ============================================================
export function handleMesecniGL(input) {
  if (!input.files.length) return;
  const f = input.files[0]; input.value = '';
  if (!f.name.match(/\.xlsx?$/i)) { toast('Nepodržan format (.xlsx/.xls)', 'err'); return; }
  const r = new FileReader();
  r.onload = e => {
    try {
      const wb = XLSX.read(e.target.result, { type: 'array', cellDates: true });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: true, defval: null });
      if (rows.length < 2) { toast('Excel je prazan ili ima samo zaglavlje', 'err'); return; }
      // Pronađi red zaglavlja (knjigovođin export često ima naslovne redove iznad — npr. "Zaključni list na dan...")
      let hdrIdx = rows.findIndex(r => Array.isArray(r) && r.some(h => h && /konto|šifra|sifra|account|^kto$/i.test(String(h).trim())));
      if (hdrIdx < 0) hdrIdx = 0;
      const hdr = rows[hdrIdx];
      const ci = (names) => { for (const n of names) { const i = hdr.findIndex(h => h && String(h).trim().toLowerCase().includes(n)); if (i >= 0) return i; } return -1; };
      const ci2 = (a, b) => hdr.findIndex(h => { const s = h ? String(h).trim().toLowerCase() : ''; return s.includes(a) && s.includes(b); });
      const findAny = (...idxs) => { for (const i of idxs) if (i >= 0) return i; return -1; };
      const C = {
        rbr: ci(['rbr', 'redni broj', 'r.br', 'r. br']),
        konto: ci(['konto', 'šifra', 'sifra', 'account', 'kto', 'broj konta']),
        naziv: ci(['naziv', 'opis', 'name', 'opis konta']),
        // Dvokolonski saldo (bruto bilans: "Saldo Duguje" / "Saldo Potražuje") — obavezno razdvojiti,
        // inače kreditni saldo (obaveze/prihodi) ispadne 0 ako se uzme samo prva "saldo" kolona.
        salDug: findAny(ci2('saldo', 'dug'), ci2('saldo', 'debit')),
        salPot: findAny(ci2('saldo', 'potraž'), ci2('saldo', 'potraz'), ci2('saldo', 'kredit'), ci2('saldo', 'credit')),
        sal: ci(['saldo', 'balance', 'stanje']),
        dug: findAny(ci2('promet', 'dug'), hdr.findIndex(h => { const s = h ? String(h).trim().toLowerCase() : ''; return (s.includes('duguje') || s.includes('debit')) && !s.includes('saldo'); })),
        pot: findAny(ci2('promet', 'potraž'), ci2('promet', 'potraz'), hdr.findIndex(h => { const s = h ? String(h).trim().toLowerCase() : ''; return (s.includes('potraž') || s.includes('potraz') || s.includes('credit')) && !s.includes('saldo'); }))
      };
      if (C.konto < 0) { toast('Excel nema kolonu "Konto/Šifra". Proverite zaglavlje.', 'err'); return; }
      // Opseg lista (sheet_to_json vraća redove relativno na !ref) — za tačno čitanje cell.w konta
      const rng = sheet['!ref'] ? XLSX.utils.decode_range(sheet['!ref']) : { s: { r: 0, c: 0 } };
      const out = [];
      for (let i = hdrIdx + 1; i < rows.length; i++) {
        const row = rows[i]; if (!row || row.every(c => c == null || c === '')) continue;
        const kRaw = row[C.konto]; if (kRaw == null || String(kRaw).trim() === '') continue;
        // Koristi formatiran tekst ćelije (cell.w) da se sačuvaju vodeće nule (klasa 0: 024, 014...) kad je konto numerička ćelija
        const kCell = sheet[XLSX.utils.encode_cell({ r: rng.s.r + i, c: rng.s.c + C.konto })];
        const konto = ((kCell && kCell.w != null) ? String(kCell.w) : String(kRaw)).trim().replace(/\s+/g, '');
        if (!/^\d/.test(konto)) continue; // mora počinjati cifrom (preskoči total/zaglavlja)
        const dug = C.dug >= 0 ? pNum(row[C.dug]) : 0;
        const pot = C.pot >= 0 ? pNum(row[C.pot]) : 0;
        // saldo = duguje − potražuje konvencija (obaveze/prihodi NEGATIVNI), kao calculate_balance_sheet
        let sal;
        if (C.salDug >= 0 && C.salPot >= 0) sal = pNum(row[C.salDug]) - pNum(row[C.salPot]);
        else if (C.sal >= 0) sal = pNum(row[C.sal]);
        else sal = dug - pot;
        // Sintetički red (zbirni) ima Rbr; analitike nemaju. Ako nema Rbr kolone, tretiraj sve kao sintetiku.
        const synthetic = C.rbr >= 0 ? (row[C.rbr] != null && String(row[C.rbr]).trim() !== '') : true;
        out.push({ konto, naziv: C.naziv >= 0 && row[C.naziv] ? String(row[C.naziv]).trim() : '', duguje: dug, potrazuje: pot, saldo: sal, synthetic });
      }
      if (!out.length) { toast('Nije pronađen nijedan konto u Excel-u', 'err'); return; }
      state.mesecniGL = out;
      const info = document.getElementById('mesGlInfo');
      if (info) { info.textContent = '✓ Excel knjigovodstvo: ' + out.length + ' konta — koristi se za BS/P&L/Inventory (klikni Generiši)'; info.style.color = 'var(--suc)'; }
      toast('Učitano ' + out.length + ' konta iz Excel-a — generiši tabove');
    } catch (err) { toast('Greška pri čitanju: ' + err.message, 'err'); }
  };
  r.readAsArrayBuffer(f);
}

export function clearMesecniGL() {
  state.mesecniGL = null;
  const info = document.getElementById('mesGlInfo');
  if (info) { info.textContent = ''; }
  toast('Excel knjigovodstvo uklonjeno — koristi se baza');
}

// ============================================================
// MESEČNI DASHBOARD PDF (grafikoni) — finansijski pregled, dvojezično, ECOR logo
// Podaci: Excel knjigovodstvo (state.mesecniGL) ako postoji, inače calculate_balance_sheet.
// ============================================================
export async function exportMesecniDashboardPdf() {
  if (!state.CU) { toast('Niste prijavljeni', 'err'); return; }
  const { t, pickLanguage } = await import('./i18n.js');
  const lang = await pickLanguage('sr'); if (!lang) return;
  showLoading();
  try {
    const { buildDashboardPdf, donutConfig, hbarConfig, barConfig } = await import('./chart-pdf.js');
    const month = parseInt(document.getElementById('cfMonth').value) || (new Date().getMonth() || 12);
    const year = parseInt(document.getElementById('cfYear').value) || new Date().getFullYear();
    let kurs = parseFloat(document.getElementById('cfKurs').value); if (!(kurs > 0)) kurs = 108;
    // Valuta prati jezik: srpski PDF = RSD, engleski PDF = USD
    const isEn = lang === 'en'; const CUR = isEn ? 'USD' : 'RSD';
    const cv = v => isEn ? v / kurs : v; const money = v => fNum2(cv(v));
    let bs;
    if (state.mesecniGL && state.mesecniGL.length) bs = state.mesecniGL.map(r => ({ ...r }));
    else { const res = await sb.rpc('calculate_balance_sheet', { p_user_id: state.CU.id, p_year: year, p_month: month, p_kurs: kurs }); if (res.error) throw res.error; bs = res.data || []; }
    // Zvanični BS/P&L u valuti PDF-a (sr=RSD → kurs 1; en=USD → /kurs)
    const cvKurs = isEn ? kurs : 1;
    const bsRows = buildOfficialBS(bs, cvKurs);
    const plRows = buildOfficialPL(bs, cvKurs);
    const g = (rows, label) => { const r = rows.find(x => x.label === label); return r ? (r.usd || 0) : 0; };
    const cash = g(bsRows, 'Cash & Banks');
    const recv = g(bsRows, 'Account Receivables') + g(bsRows, 'Account Receivables Interco.');
    const inventory = g(bsRows, 'Inventory');
    const totalAssets = g(bsRows, 'TOTAL ASSETS'), totalLiab = g(bsRows, 'TOTAL LIABILITIES'), totalEq = g(bsRows, 'TOTAL EQUITY');
    const fixedAssets = g(bsRows, 'FIXED ASSETS'), intang = g(bsRows, 'Intangible Assets'), curAssets = g(bsRows, 'CURRENT ASSETS');
    const totalRev = g(plRows, 'Total revenue'), cogs = g(plRows, 'Cost of goods sold'), grossProfit = g(plRows, 'Gross profit');
    const opex = g(plRows, 'Total operating expenses'), da = g(plRows, 'Depreciation and amortization'), net = g(plRows, 'Net Income');
    const pay = -bs.filter(r => { const k = String(r.konto || ''); return (k.startsWith('435') || k.startsWith('436')) && r.synthetic !== false; }).reduce((s, r) => s + parseFloat(r.saldo || 0), 0);
    const fmt = v => fNum2(v) + ' ' + CUR;
    const payItems = bs.filter(r => { const k = String(r.konto || ''); return (k.startsWith('435') || k.startsWith('436')) && r.naziv; }).map(r => ({ name: r.naziv, v: cv(-parseFloat(r.saldo || 0)) })).filter(x => x.v > 0).sort((a, b) => b.v - a.v).slice(0, 8);
    // Cash Flow (iz knjiženja) — prilivi/odlivi za grafikon
    let cfInc = 0, cfExp = 0;
    try { const cfRes = await sb.rpc('calculate_cashflow', { p_user_id: state.CU.id, p_year: year, p_month: month, p_kurs: kurs }); if (!cfRes.error && cfRes.data) { const sumv = o => Object.values(o || {}).reduce((s, v) => s + (parseFloat(v) || 0), 0); cfInc = cv(sumv(cfRes.data.income)); cfExp = cv(sumv(cfRes.data.expenses)); } } catch (_) {}
    const period = state.mesecniGL ? 'Zaključni list (Excel)' : (MONTHS_SR[month - 1] + ' ' + year);

    const kpis = [
      { label: t('cash_position', lang), value: fmt(cash), sub: '', color: 'green' },
      { label: isEn ? 'Total Assets' : 'Ukupna aktiva', value: fmt(totalAssets), sub: '', color: 'gold' },
      { label: t('vendor_debt_today', lang), value: fmt(cv(pay)), sub: '', color: 'red' },
      { label: t('net_income', lang), value: (net >= 0 ? '+' : '') + fmt(net), sub: '', color: net >= 0 ? 'green' : 'red' },
    ];
    const charts = [
      { span: 1, config: donutConfig([isEn ? 'Current' : 'Obrtna', isEn ? 'Fixed' : 'Stalna', 'Intangible'], [curAssets, fixedAssets, intang], { colors: ['#2b9d8f', '#227846', '#c19a40'], title: (isEn ? 'Assets' : 'Aktiva') }) },
      { span: 1, config: donutConfig([t('liabilities', lang), t('equity', lang)], [totalLiab, totalEq], { colors: ['#c94048', '#c19a40'], title: (isEn ? 'Liabilities & Equity' : 'Pasiva i kapital') }) },
      { span: 1, config: barConfig([t('revenue', lang), 'COGS', 'OpEx', t('net_income', lang)], [{ label: CUR, data: [totalRev, cogs, opex, net], backgroundColor: ['#227846', '#c94048', '#d08030', '#c19a40'] }], { title: t('pnl', lang) }) },
    ];
    const pick = (rows, labels) => labels.map(L => ({ name: L, v: g(rows, L) })).filter(x => x.v > 0.5).sort((a, b) => b.v - a.v);
    const cogsBreak = pick(plRows, ['Raw Materials Costs', 'Direct Labor Costs', 'Energy Costs', 'Maintenance Costs', 'Manufacturing costs']);
    if (cogsBreak.length) charts.push({ span: 1, config: hbarConfig(cogsBreak.map(x => x.name), cogsBreak.map(x => +x.v.toFixed(2)), { title: 'COGS', color: '#c94048' }) });
    const opexBreak = pick(plRows, ['Administration & HR', 'Transportation & Logistics Costs', 'Other SG&A', 'Marketing & Supply Chain', 'Quality Control', 'Environmental Protection', 'Insurance Costs']);
    if (opexBreak.length) charts.push({ span: 1, config: hbarConfig(opexBreak.map(x => x.name), opexBreak.map(x => +x.v.toFixed(2)), { title: 'OpEx', color: '#d08030' }) });
    if (cfInc || cfExp) charts.push({ span: 1, config: barConfig([isEn ? 'Inflows' : 'Prilivi', isEn ? 'Outflows' : 'Odlivi'], [{ label: CUR, data: [cfInc, cfExp], backgroundColor: ['#227846', '#c94048'] }], { title: isEn ? 'Cash Flow' : 'Tok gotovine' }) });
    if (payItems.length) charts.push({ span: 1, config: hbarConfig(payItems.map(p => p.name), payItems.map(p => +p.v.toFixed(2)), { title: t('payables', lang) + ' (435/436)', color: '#c94048' }) });

    // Pune tabele BS i P&L
    const rowsToTable = rows => rows.map(r => r.type === 'head' ? ['— ' + r.label + ' —', ''] : [r.label, (r.usd != null ? fNum2(r.usd) : '')]);
    const tables = [
      { title: t('balance_sheet', lang) + ' (' + CUR + ')', head: [isEn ? 'Item' : 'Stavka', CUR], rows: rowsToTable(bsRows), aligns: ['left', 'right'] },
      { title: t('pnl', lang) + ' (' + CUR + ')', head: [isEn ? 'Item' : 'Stavka', CUR], rows: rowsToTable(plRows), aligns: ['left', 'right'] },
    ];
    if (payItems.length) tables.push({ title: t('payables', lang) + ' (435/436)', head: [t('komitent', lang), CUR], rows: payItems.map(p => [p.name, fNum2(p.v)]), aligns: ['left', 'right'] });

    const takeaways = [
      { level: net >= 0 ? 'ok' : 'critical', text: t('net_income', lang) + ': ' + fmt(net) + ' (' + (isEn ? 'Revenue' : 'Prihod') + ' ' + fmt(totalRev) + ' − COGS ' + fmt(cogs) + ' − OpEx ' + fmt(opex) + ' − D&A ' + fmt(da) + ').' },
      { level: cash < pay ? 'critical' : 'info', text: t('cash_position', lang) + ': ' + fmt(cash) + ' · ' + t('vendor_debt_today', lang) + ': ' + fmt(cv(pay)) + '.' },
      { level: 'info', text: (isEn ? 'Total Assets ' : 'Ukupna aktiva ') + fmt(totalAssets) + ' = ' + (isEn ? 'Liab ' : 'Obaveze ') + fmt(totalLiab) + ' + ' + (isEn ? 'Equity ' : 'Kapital ') + fmt(totalEq) + '.' },
    ];

    await buildDashboardPdf({
      reportTitle: t('financial_overview', lang),
      subtitle: t('monthly_report', lang) + (state.mesecniGL ? ' · Excel' : ''),
      period, kurs, currency: CUR, kpis, charts, tables, takeaways,
      filename: 'ECOR_Monthly_' + (state.mesecniGL ? 'GL' : (year + '-' + String(month).padStart(2, '0'))) + '_' + lang + '.pdf',
    }, lang);
    if (state.CU) sb.rpc('log_audit', { p_user_id: state.CU.id, p_action: 'export_pdf', p_entity_type: 'mesecni', p_entity_name: 'dashboard_pdf' }).then(null, e => console.error('SBIS:', e));
    toast('Mesečni PDF (grafikoni) izvezen');
  } catch (e) { toast('Greška PDF: ' + (e.message || e), 'err'); console.error(e); } finally { hideLoading(); }
}

// ============================================================
// SHARED EXCEL STYLES
// ============================================================
const XH = { font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 11 }, fill: { fgColor: { rgb: '0F1923' } }, alignment: { horizontal: 'center' } };
const XN = { font: { sz: 10 }, numFmt: '#,##0.000' };
const XB = { font: { bold: true, sz: 11 }, numFmt: '#,##0.000', fill: { fgColor: { rgb: 'FEF08A' } } };
const XT = { font: { bold: true, sz: 12, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: '0F1923' } }, numFmt: '#,##0.000' };
const XS = { font: { bold: true, sz: 11, color: { rgb: 'D4A843' } }, fill: { fgColor: { rgb: '1A2332' } } };
function xc(v, s) { return { v, s: s || {}, t: typeof v === 'number' ? 'n' : 's' }; }
function applyXs(ws, rows) { rows.forEach((r, ri) => r.forEach((cell, ci) => { const a = XLSX.utils.encode_cell({ r: ri, c: ci }); if (ws[a]) ws[a].s = cell.s; })); }

// ============================================================
// COMBINED EXPORT — 4 sheets in 1 Excel
// ============================================================
function buildCfWs(cf, kurs, monthName) {
  const o = { rsd: 0, usd: 0, ...(cf.opening || {}) };
  const inc = { customer_collection_rsd: 0, customer_collection_usd: 0, tax_refund: 0, recapitalization: 0, ...(cf.income || {}) };
  const exp = { payment_suppliers: 0, suppliers_advance: 0, fix_asset_advance: 0, salaries: 0, import_taxes: 0, bank_commission: 0, gov_taxes: 0, others: 0, ...(cf.expenses || {}) };
  const tax = { monthly_vat: 0, retained_vat: 0, ...(cf.taxes || {}) };
  const rows = [];
  rows.push([xc('ECOR SERBIA', XH), xc('', XH), xc('', XH), xc('', XH), xc('', XH), xc('', XH)]);
  rows.push([xc('CASH FLOW — ' + monthName, { font: { bold: true, sz: 12 } }), xc('', {}), xc('', {}), xc('', {}), xc('', {}), xc('', {})]);
  rows.push([xc('', {}), xc('', {}), xc('RSD', XH), xc('RSD→USD', XH), xc('USD', XH), xc('Tot. USD', XH)]);

  // *_usd i recapitalization su istorijski RSD — mergovano u DIN kolonu
  const openDin = (o.rsd || 0) + (o.usd || 0);
  const openDinUsd = openDin / kurs;
  rows.push([xc('', {}), xc('BALANCE', XB), xc(openDin, XB), xc(openDinUsd, XB), xc(null, XB), xc(openDinUsd, XB)]);
  rows.push([xc('( + )', { font: { bold: true } }), xc('INCOME', { font: { bold: true } }), xc('', {}), xc('', {}), xc('', {}), xc('', {})]);

  const ccDin = (inc.customer_collection_rsd || 0) + (inc.customer_collection_usd || 0);
  const ccDinUsd = ccDin / kurs;
  rows.push([xc('', {}), xc('Customer Collection', XN), xc(ccDin, XN), xc(ccDinUsd, XN), xc(null, XN), xc(ccDinUsd, XN)]);
  const recapDin = inc.recapitalization || 0;
  const recapDinUsd = recapDin / kurs;
  rows.push([xc('', {}), xc('Recapitalization', XN), xc(recapDin, XN), xc(recapDinUsd, XN), xc(null, XN), xc(recapDinUsd, XN)]);
  rows.push([xc('', {}), xc('Tax Refund & Other', XN), xc(inc.tax_refund, XN), xc(inc.tax_refund / kurs, XN), xc(null, XN), xc(inc.tax_refund / kurs, XN)]);

  const tID = ccDin + recapDin + inc.tax_refund;
  rows.push([xc('', {}), xc('Total Income', XB), xc(tID, XB), xc(tID / kurs, XB), xc(null, XB), xc(tID / kurs, XB)]);

  rows.push([xc('( - )', { font: { bold: true } }), xc('EXPENSES', { font: { bold: true } }), xc('', {}), xc('', {}), xc('', {}), xc('', {})]);
  rows.push([xc('', {}), xc('Payment to Suppliers', XN), xc(-exp.payment_suppliers, XN), xc(-exp.payment_suppliers / kurs, XN), xc(null, XN), xc(-exp.payment_suppliers / kurs, XN)]);
  if ((exp.suppliers_advance||0) > 0) rows.push([xc('', {}), xc('Suppliers Advance', XN), xc(-(exp.suppliers_advance||0), XN), xc(-(exp.suppliers_advance||0) / kurs, XN), xc(null, XN), xc(-(exp.suppliers_advance||0) / kurs, XN)]);
  if ((exp.fix_asset_advance||0) > 0) rows.push([xc('', {}), xc('Fix Asset Advance', XN), xc(-(exp.fix_asset_advance||0), XN), xc(-(exp.fix_asset_advance||0) / kurs, XN), xc(null, XN), xc(-(exp.fix_asset_advance||0) / kurs, XN)]);
  rows.push([xc('', {}), xc('Salaries', XN), xc(-exp.salaries, XN), xc(-exp.salaries / kurs, XN), xc(null, XN), xc(-exp.salaries / kurs, XN)]);
  if (exp.import_taxes > 0) rows.push([xc('', {}), xc('Import/Export Taxes', XN), xc(-exp.import_taxes, XN), xc(-exp.import_taxes / kurs, XN), xc(null, XN), xc(-exp.import_taxes / kurs, XN)]);
  if (exp.bank_commission > 0) rows.push([xc('', {}), xc('Bank Commission', XN), xc(-exp.bank_commission, XN), xc(-exp.bank_commission / kurs, XN), xc(null, XN), xc(-exp.bank_commission / kurs, XN)]);
  rows.push([xc('', {}), xc('Gov. Taxes & Contributions', XN), xc(-exp.gov_taxes, XN), xc(-exp.gov_taxes / kurs, XN), xc(null, XN), xc(-exp.gov_taxes / kurs, XN)]);
  if (exp.others > 0) rows.push([xc('', {}), xc('Others', XN), xc(-exp.others, XN), xc(-exp.others / kurs, XN), xc(null, XN), xc(-exp.others / kurs, XN)]);

  const tED = -(exp.payment_suppliers + (exp.suppliers_advance||0) + (exp.fix_asset_advance||0) + exp.salaries + exp.import_taxes + exp.bank_commission + exp.gov_taxes + exp.others);
  rows.push([xc('', {}), xc('Total Expenses', XB), xc(tED, XB), xc(tED / kurs, XB), xc(null, XB), xc(tED / kurs, XB)]);

  const fT = (tID + tED) / kurs;
  rows.push([xc('', {}), xc('FINAL BALANCE', XT), xc(tID + tED, XT), xc(fT, XT), xc(null, XT), xc(fT, XT)]);
  rows.push([xc('', {}), xc('', {}), xc('', {}), xc('', {}), xc('', {}), xc('', {})]);

  if (tax.monthly_vat > 0) rows.push([xc('', {}), xc('Monthly VAT', XN), xc(tax.monthly_vat, XN), xc(tax.monthly_vat / kurs, XN), xc(null, XN), xc(tax.monthly_vat / kurs, XN)]);
  rows.push([xc('', {}), xc('Retained VAT & Income Tax', XN), xc(tax.retained_vat, XN), xc(tax.retained_vat / kurs, XN), xc(null, XN), xc(tax.retained_vat / kurs, XN)]);

  const nC = fT - (tax.monthly_vat / kurs) - (tax.retained_vat / kurs);
  rows.push([xc('( = )', { font: { bold: true } }), xc('NET CASH', XT), xc(null, XT), xc(null, XT), xc(null, XT), xc(nC, XT)]);
  rows.push([xc('', {}), xc('', {}), xc('', {}), xc('', {}), xc('', {}), xc('', {})]);
  rows.push([xc('ER', { font: { bold: true } }), xc(kurs, { font: { bold: true }, numFmt: '#,##0.0000' }), xc('', {}), xc('', {}), xc('', {}), xc('', {})]);

  const ws = XLSX.utils.aoa_to_sheet(rows.map(r => r.map(c => c.v)));
  ws['!cols'] = [{ wch: 8 }, { wch: 32 }, { wch: 16 }, { wch: 14 }, { wch: 14 }, { wch: 14 }];
  applyXs(ws, rows);
  return ws;
}

function buildDataWs(data, kurs, monthName, mode) {
  const title = mode === 'inv' ? 'Inventory' : mode === 'pl' ? 'P&L' : 'Balance Sheet';
  const rows = [];
  rows.push([xc('ECOR SERBIA — ' + title + ' ' + monthName, { font: { bold: true, sz: 14 } }), xc('', {}), xc('', {}), xc('', {}), xc('', {}), xc('', {})]);
  rows.push([xc('Kurs: ' + fNum(kurs), { font: { sz: 10 } }), xc('', {}), xc('', {}), xc('', {}), xc('', {}), xc('', {})]);
  rows.push(['Šifra', 'Naziv', 'Duguje', 'Potražuje', 'Saldo', 'Saldo USD'].map(h => xc(h, XH)));
  data.sort((a, b) => a.konto.localeCompare(b.konto)).forEach(r => {
    const d = parseFloat(r.duguje || 0), cr = parseFloat(r.potrazuje || 0), s = parseFloat(r.saldo || 0);
    if (mode === 'inv' && Math.abs(s) < 0.01) return;
    rows.push([xc(r.konto, {}), xc(r.naziv || '', {}), xc(d || null, XN), xc(cr || null, XN), xc(s, XN), xc(s / kurs, XN)]);
  });
  const totalD = data.reduce((s, r) => s + parseFloat(r.duguje || 0), 0);
  const totalC = data.reduce((s, r) => s + parseFloat(r.potrazuje || 0), 0);
  const totalS = totalD - totalC;
  rows.push([xc('UKUPNO', XB), xc('', XB), xc(totalD, XB), xc(totalC, XB), xc(totalS, XB), xc(totalS / kurs, XB)]);

  const ws = XLSX.utils.aoa_to_sheet(rows.map(r => r.map(c => c.v)));
  ws['!cols'] = [{ wch: 12 }, { wch: 40 }, { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 14 }];
  applyXs(ws, rows);
  return ws;
}

// Zvanični BS/P&L sheet iz strukture buildOfficialBS/buildOfficialPL ({label,usd,type})
function buildOfficialWs(rows, title) {
  const out = [];
  out.push([xc(title, { font: { bold: true, sz: 13 } }), xc('USD', XH)]);
  for (const r of rows) {
    if (r.type === 'head') { out.push([xc(r.label, XS), xc('', XS)]); }
    else {
      const sub = r.type === 'sub';
      out.push([xc(r.label, sub ? { font: { bold: true } } : {}), xc(Math.round((r.usd || 0) * 100) / 100, sub ? XB : XN)]);
    }
  }
  const ws = XLSX.utils.aoa_to_sheet(out.map(r => r.map(c => c.v)));
  ws['!cols'] = [{ wch: 46 }, { wch: 18 }];
  applyXs(ws, out);
  return ws;
}

export async function exportMesecniXlsx() {
  if (!state.CU) { toast('Niste prijavljeni', 'err'); return; }
  const month = parseInt(document.getElementById('cfMonth').value);
  const year = parseInt(document.getElementById('cfYear').value);
  const kurs = parseFloat(document.getElementById('cfKurs').value) || 108;
  const monthName = MONTHS_SR[month - 1] + ' ' + year;

  showLoading();
  try {
    const [cfRes, bsRes] = await Promise.all([
      sb.rpc('calculate_cashflow', { p_user_id: state.CU.id, p_year: year, p_month: month, p_kurs: kurs }),
      sb.rpc('calculate_balance_sheet', { p_user_id: state.CU.id, p_year: year, p_month: month, p_kurs: kurs })
    ]);
    if (cfRes.error) throw cfRes.error;
    if (bsRes.error) throw bsRes.error;

    // BS/P&L: koristi uvezeni Zaključni list ako postoji, inače DB (calculate_balance_sheet)
    const bsData = (state.mesecniGL && state.mesecniGL.length) ? state.mesecniGL : (bsRes.data || []);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, buildCfWs(cfRes.data, kurs, monthName), 'Cash Flow');
    XLSX.utils.book_append_sheet(wb, buildOfficialWs(buildOfficialBS(bsData, kurs), 'Balance Sheet — ' + monthName), 'Balance Sheet');
    XLSX.utils.book_append_sheet(wb, buildOfficialWs(buildOfficialPL(bsData, kurs), 'P&L — ' + monthName), 'P&L');
    XLSX.utils.book_append_sheet(wb, buildDataWs(bsData.filter(r => String(r.konto).startsWith('1') && r.synthetic !== false), kurs, monthName, 'inv'), 'Inventory');

    XLSX.writeFile(wb, 'Monthly_Report_' + monthName.replace(' ', '_') + '.xlsx', { bookSST: false, cellStyles: true });
    toast('Mesečni izveštaj eksportovan (Cash Flow, Balance Sheet, P&L, Inventory)');
  } catch (e) { toast('Greška: ' + (e.message || e), 'err'); }
  hideLoading();
}

// ============================================================
// YEARLY CASHFLOW EXPORT — 12 sheets in 1 Excel
// ============================================================
export async function exportCashflowYearXlsx() {
  if (!state.CU) { toast('Niste prijavljeni', 'err'); return; }
  const year = parseInt(document.getElementById('cfYear').value);
  const kurs = parseFloat(document.getElementById('cfKurs').value) || 108;

  showLoading();
  try {
    const promises = [];
    for (let m = 1; m <= 12; m++) {
      promises.push(sb.rpc('calculate_cashflow', { p_user_id: state.CU.id, p_year: year, p_month: m, p_kurs: kurs }));
    }
    const results = await Promise.all(promises);
    const wb = XLSX.utils.book_new();
    const sheetNames = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];
    let hasData = false;
    for (let m = 0; m < 12; m++) {
      if (results[m].error) continue;
      const cf = results[m].data;
      if (!cf) continue;
      const monthName = MONTHS_SR[m] + ' ' + year;
      XLSX.utils.book_append_sheet(wb, buildCfWs(cf, kurs, monthName), sheetNames[m] + ' Cash Flow');
      hasData = true;
    }
    if (!hasData) { toast('Nema podataka za ' + year, 'err'); hideLoading(); return; }
    XLSX.writeFile(wb, 'Cashflow_' + year + '.xlsx', { bookSST: false, cellStyles: true });
    toast('Godišnji cashflow eksportovan (' + wb.SheetNames.length + ' meseci)');
  } catch (e) { toast('Greška: ' + (e.message || e), 'err'); }
  hideLoading();
}

// ============================================================
// BS 12 MONTHS
// ============================================================
let lastBs12 = null;
const MSH = ['Jan', 'Feb', 'Mar', 'Apr', 'Maj', 'Jun', 'Jul', 'Avg', 'Sep', 'Okt', 'Nov', 'Dec'];

function calcCat(mData, testFn) {
  return (mData || []).filter(r => testFn(r.konto)).reduce((s, r) => s + parseFloat(r.saldo || 0), 0);
}

// Detailed BS 12M categories matching original Excel format
const BS12_CATS = [
  // Current Assets
  { key: 'cash', label: 'Cash & Banks', test: k => k.startsWith('241') || k.startsWith('244') },
  { key: 'recv', label: 'Account Receivables', test: k => k.startsWith('204') || k.startsWith('205') },
  { key: 'recvIC', label: 'Account Receivables Interco.', test: k => k.startsWith('200') || k.startsWith('201') || k.startsWith('202') || k.startsWith('203') },
  { key: 'inv', label: 'Inventory', test: k => k.startsWith('1') && !k.startsWith('15') },
  { key: 'oca', label: 'Other Current Assets', test: k => (k.startsWith('2') && !k.startsWith('241') && !k.startsWith('244') && !k.startsWith('204') && !k.startsWith('205') && !k.startsWith('200') && !k.startsWith('201') && !k.startsWith('202') && !k.startsWith('203')) || k.startsWith('15') || k.startsWith('00') },
  // Fixed Assets
  { key: 'build', label: 'Buildings and Construction in progress', test: k => k.startsWith('021') || k.startsWith('024') },
  { key: 'mach', label: 'Plant Machinery & Equipment', test: k => k.startsWith('022') || k.startsWith('023') },
  { key: 'land', label: 'Land, Plot, Buildings', test: k => k.startsWith('020') },
  { key: 'ppeAdv', label: 'Advances for PP&E', test: k => k.startsWith('09') },
  { key: 'bio', label: 'Biological Agents', test: k => k.startsWith('03') },
  { key: 'depr', label: 'Accum. Depreciation', test: k => k.startsWith('014') || k.startsWith('029') },
  { key: 'oFixed', label: 'Other Fixed Assets', test: k => (k.startsWith('025') || k.startsWith('026') || k.startsWith('027') || k.startsWith('028') || k.startsWith('04') || k.startsWith('05') || k.startsWith('06') || k.startsWith('07') || k.startsWith('08')) && !k.startsWith('09') },
  // Intangible
  { key: 'intang', label: 'Intangible Assets', test: k => k.startsWith('01') && !k.startsWith('014') },
  // Liabilities - Current
  { key: 'payTrade', label: 'Account Payables', test: k => k.startsWith('435') || k.startsWith('436') },
  { key: 'accrued', label: 'Accrued Expenses', test: k => k.startsWith('4') && !k.startsWith('435') && !k.startsWith('436') && !k.startsWith('40') && !k.startsWith('41') && !k.startsWith('42') },
  // Liabilities - Long term
  { key: 'ltl', label: 'Long Term Liabilities', test: k => k.startsWith('40') || k.startsWith('41') || k.startsWith('42') },
  // Equity
  { key: 'capital', label: 'Capital', test: k => k.startsWith('30') || k.startsWith('31') || k.startsWith('32') || k.startsWith('33') },
  { key: 'retEarn', label: 'Previous Period Gain/Loss', test: k => k.startsWith('34') || k.startsWith('35') || k.startsWith('36') || k.startsWith('37') || k.startsWith('38') || k.startsWith('39') },
  // P&L accounts for current period
  { key: '_rev', label: '_rev', test: k => k.startsWith('6') },
  { key: '_exp', label: '_exp', test: k => k.startsWith('5') },
  // Class 7 — opening/closing (usually nets to 0)
  { key: '_cl7', label: '_cl7', test: k => k.startsWith('7') },
];

function computeBS12(monthsData) {
  const v = {};
  BS12_CATS.forEach(it => { v[it.key] = []; for (let m = 0; m < 12; m++) v[it.key].push(calcCat(monthsData[m], it.test)); });
  v.curAssets = []; v.fixAssets = []; v.totalA = [];
  v.curLiab = []; v.totalL = []; v.curPeriodGL = []; v.totalEq = []; v.totalLE = []; v.check = [];
  for (let m = 0; m < 12; m++) {
    v.curAssets.push(v.cash[m] + v.recv[m] + v.recvIC[m] + v.inv[m] + v.oca[m]);
    v.fixAssets.push(v.build[m] + v.mach[m] + v.land[m] + v.ppeAdv[m] + v.bio[m] + v.depr[m] + v.oFixed[m]);
    v.totalA.push(v.curAssets[m] + v.fixAssets[m] + v.intang[m]);
    v.curLiab.push(-(v.payTrade[m] + v.accrued[m]));
    v.totalL.push(v.curLiab[m] + (-v.ltl[m]));
    v.curPeriodGL.push(-v._rev[m] - v._exp[m]);
    v.totalEq.push(-v.capital[m] - v.retEarn[m] + v.curPeriodGL[m]);
    v.totalLE.push(v.totalL[m] + v.totalEq[m]);
    v.check.push(v.totalA[m] - v.totalLE[m] + (v._cl7[m] || 0));
  }
  return v;
}

export async function generateBS12() {
  if (!state.CU) { toast('Niste prijavljeni', 'err'); return; }
  const year = parseInt(document.getElementById('bs12Year').value);
  const kurs = parseFloat(document.getElementById('bs12Kurs').value) || 108;

  showLoading();
  try {
    const promises = [];
    for (let m = 1; m <= 12; m++) {
      promises.push(sb.rpc('calculate_balance_sheet', { p_user_id: state.CU.id, p_year: year, p_month: m, p_kurs: kurs }));
    }
    const results = await Promise.all(promises);
    results.forEach((r, i) => { if (r.error) console.warn('Month ' + (i+1) + ' error:', r.error); });
    const monthsData = results.map(r => r.data || []);
    lastBs12 = { year, kurs, monthsData };
    renderBS12(monthsData, kurs, year);
    document.getElementById('bs12ExpBtn').style.display = '';
    toast('BS 12 Months generisan za ' + year);
  } catch (e) { toast('Greška: ' + (e.message || e), 'err'); }
  hideLoading();
}

function renderBS12(monthsData, kurs, year) {
  const out = document.getElementById('bs12Output'); out.textContent = '';
  const v = computeBS12(monthsData);

  const rows = [
    { type: 'sec', label: 'ASSETS', color: 'var(--acc)' },
    { type: 'row', label: 'Cash & Banks', vals: v.cash, sign: 1 },
    { type: 'row', label: 'Account Receivables', vals: v.recv, sign: 1 },
    { type: 'row', label: 'Account Receivables Interco.', vals: v.recvIC, sign: 1 },
    { type: 'row', label: 'Inventory', vals: v.inv, sign: 1 },
    { type: 'row', label: 'Other Current Assets', vals: v.oca, sign: 1 },
    { type: 'sub', label: 'CURRENT ASSETS', vals: v.curAssets, color: 'var(--acc)' },
    { type: 'gap' },
    { type: 'row', label: 'Buildings and Construction in progress', vals: v.build, sign: 1 },
    { type: 'row', label: 'Plant Machinery & Equipment', vals: v.mach, sign: 1 },
    { type: 'row', label: 'Land, Plot, Buildings', vals: v.land, sign: 1 },
    { type: 'row', label: 'Advances for PP&E', vals: v.ppeAdv, sign: 1 },
    { type: 'row', label: 'Biological Agents', vals: v.bio, sign: 1 },
    { type: 'row', label: 'Accum. Depreciation', vals: v.depr, sign: 1, neg: true },
    { type: 'row', label: 'Other Fixed Assets', vals: v.oFixed, sign: 1 },
    { type: 'sub', label: 'FIXED ASSETS', vals: v.fixAssets, color: 'var(--acc)' },
    { type: 'gap' },
    { type: 'row', label: 'Intangible Assets', vals: v.intang, sign: 1 },
    { type: 'tot', label: 'TOTAL ASSETS', vals: v.totalA, color: 'var(--acc)' },
    { type: 'gap' },
    { type: 'sec', label: 'LIABILITIES', color: 'var(--dng)' },
    { type: 'row', label: 'Account Payables', vals: v.payTrade, sign: -1 },
    { type: 'row', label: 'Accrued Expenses', vals: v.accrued, sign: -1 },
    { type: 'sub', label: 'CURRENT LIABILITIES', vals: v.curLiab, color: 'var(--dng)' },
    { type: 'row', label: 'Long Term Liabilities', vals: v.ltl, sign: -1 },
    { type: 'tot', label: 'TOTAL LIABILITIES', vals: v.totalL, color: 'var(--dng)' },
    { type: 'gap' },
    { type: 'sec', label: 'EQUITY', color: 'var(--suc)' },
    { type: 'row', label: 'Capital', vals: v.capital, sign: -1 },
    { type: 'row', label: 'Previous Period Gain/Loss', vals: v.retEarn, sign: -1 },
    { type: 'row', label: 'Gain/Loss Current Period', vals: v.curPeriodGL, sign: 1 },
    { type: 'tot', label: 'TOTAL EQUITY', vals: v.totalEq, color: 'var(--suc)' },
    { type: 'gap' },
    { type: 'tot', label: 'TOTAL LIABILITIES + EQUITY', vals: v.totalLE, color: 'var(--tx)' },
    { type: 'chk', label: 'CHECK (A \u2212 L \u2212 E)', vals: v.check },
  ];

  // Card
  const card = document.createElement('div'); card.className = 'card'; card.style.cssText = 'padding:0;overflow-x:auto';
  const hdr = document.createElement('div'); hdr.style.cssText = 'padding:16px 20px;border-bottom:2px solid var(--acc)';
  const h2 = document.createElement('h2'); h2.style.cssText = 'font-size:24px;color:var(--acc)'; h2.textContent = 'BALANCE SHEET 12 MONTHS \u2014 ' + year;
  const sub = document.createElement('div'); sub.style.cssText = 'font-size:13px;color:var(--tx3);margin-top:2px'; sub.textContent = 'ECOR SERBIA \u00B7 Kurs: ' + fNum(kurs);
  hdr.appendChild(h2); hdr.appendChild(sub); card.appendChild(hdr);

  // border-separate is required for sticky to work on td elements
  const tbl = document.createElement('table'); tbl.style.cssText = 'border-collapse:separate;border-spacing:0;table-layout:auto;width:max-content;min-width:100%';
  const stickyBase = 'position:sticky;left:0;z-index:1;min-width:220px;white-space:nowrap;box-shadow:4px 0 8px -2px rgba(0,0,0,0.4)';

  // Thead
  const thead = document.createElement('thead');
  const thr = document.createElement('tr');
  const th0 = document.createElement('th'); th0.style.cssText = 'padding:12px 16px;text-align:left;font-size:13px;color:var(--tx3);background:var(--raised);z-index:2;border-bottom:2px solid var(--border);border-right:2px solid var(--border);' + stickyBase; th0.textContent = '';
  thr.appendChild(th0);
  MSH.forEach((m, i) => { const th = document.createElement('th'); th.textContent = m; th.style.cssText = 'padding:12px 14px;font-size:13px;text-align:right;color:var(--tx3);text-transform:uppercase;letter-spacing:.5px;white-space:nowrap;background:var(--raised);border-bottom:2px solid var(--border)' + (i < 11 ? ';border-right:1px solid var(--border-lt)' : ''); thr.appendChild(th); });
  thead.appendChild(thr); tbl.appendChild(thead);

  const tbody = document.createElement('tbody');
  rows.forEach(r => {
    if (r.type === 'gap') {
      const tr = document.createElement('tr');
      const tdS = document.createElement('td'); tdS.style.cssText = 'height:8px;background:var(--raised);border-right:2px solid var(--border);' + stickyBase;
      const tdR = document.createElement('td'); tdR.colSpan = 12; tdR.style.height = '8px';
      tr.appendChild(tdS); tr.appendChild(tdR); tbody.appendChild(tr); return;
    }
    if (r.type === 'sec') {
      const tr = document.createElement('tr');
      const tdS = document.createElement('td'); tdS.style.cssText = 'padding:12px 16px;font-weight:700;font-size:16px;color:' + r.color + ';background:var(--surface);border-right:2px solid var(--border);' + stickyBase;
      tdS.textContent = r.label;
      const tdR = document.createElement('td'); tdR.colSpan = 12; tdR.style.cssText = 'background:var(--surface)';
      tr.appendChild(tdS); tr.appendChild(tdR); tbody.appendChild(tr); return;
    }

    const tr = document.createElement('tr');
    const isSub = r.type === 'sub';
    const isTot = r.type === 'tot';
    const bg = (isTot || isSub) ? 'var(--surface)' : 'var(--raised)';
    const tdL = document.createElement('td'); tdL.style.cssText = 'padding:10px 16px;font-size:14px;background:' + bg + ';border-right:2px solid var(--border);border-bottom:1px solid var(--border-lt);' + stickyBase;
    if (r.type === 'row') { tdL.style.paddingLeft = '28px'; tdL.style.color = r.neg ? 'var(--dng)' : 'var(--tx)'; }
    if (isTot) { tdL.style.fontWeight = '700'; tdL.style.color = r.color; tdL.style.fontSize = '15px'; }
    if (isSub) { tdL.style.fontWeight = '600'; tdL.style.color = r.color; tdL.style.fontStyle = 'italic'; }
    if (r.type === 'chk') { tdL.style.fontWeight = '700'; tdL.style.fontStyle = 'italic'; tdL.style.color = 'var(--tx3)'; }
    tdL.textContent = r.label; tr.appendChild(tdL);

    for (let m = 0; m < 12; m++) {
      const td = document.createElement('td'); td.style.cssText = 'padding:10px 14px;text-align:right;font-family:var(--mn);font-size:14px;white-space:nowrap;border-bottom:1px solid var(--border-lt)' + (m < 11 ? ';border-right:1px solid var(--border-lt)' : '');
      let val = r.type === 'row' ? r.vals[m] * r.sign : r.vals[m];
      if (isTot) { td.style.fontWeight = '700'; td.style.background = 'var(--surface)'; td.style.borderTop = '2px solid var(--border)'; td.style.color = r.color; }
      if (isSub) { td.style.fontWeight = '600'; td.style.background = 'var(--surface)'; td.style.borderTop = '1px solid var(--border)'; td.style.color = r.color; td.style.fontStyle = 'italic'; }
      if (r.type === 'chk') { td.style.fontStyle = 'italic'; td.style.color = Math.abs(val) < 1 ? 'var(--suc)' : 'var(--dng)'; }
      td.textContent = fNum(val);
      tr.appendChild(td);
    }
    tbody.appendChild(tr);
  });
  tbl.appendChild(tbody); card.appendChild(tbl); out.appendChild(card);
}

// Subtotal Excel style
const XSB = { font: { bold: true, sz: 10, italic: true }, numFmt: '#,##0.000', fill: { fgColor: { rgb: 'E8E2D0' } } };

export function exportBs12Xlsx() {
  if (!lastBs12) { toast('Generišite BS 12 Months prvo', 'err'); return; }
  const { year, kurs, monthsData } = lastBs12;
  const v = computeBS12(monthsData);

  const xlRows = [
    { label: 'ASSETS', type: 'sec' },
    { label: '  Cash & Banks', vals: v.cash, sign: 1 },
    { label: '  Account Receivables', vals: v.recv, sign: 1 },
    { label: '  Account Receivables Interco.', vals: v.recvIC, sign: 1 },
    { label: '  Inventory', vals: v.inv, sign: 1 },
    { label: '  Other Current Assets', vals: v.oca, sign: 1 },
    { label: '  CURRENT ASSETS', vals: v.curAssets, type: 'sub' },
    { label: '', type: 'gap' },
    { label: '  Buildings and Construction', vals: v.build, sign: 1 },
    { label: '  Plant Machinery & Equipment', vals: v.mach, sign: 1 },
    { label: '  Land, Plot, Buildings', vals: v.land, sign: 1 },
    { label: '  Advances for PP&E', vals: v.ppeAdv, sign: 1 },
    { label: '  Biological Agents', vals: v.bio, sign: 1 },
    { label: '  Accum. Depreciation', vals: v.depr, sign: 1 },
    { label: '  Other Fixed Assets', vals: v.oFixed, sign: 1 },
    { label: '  FIXED ASSETS', vals: v.fixAssets, type: 'sub' },
    { label: '', type: 'gap' },
    { label: '  Intangible Assets', vals: v.intang, sign: 1 },
    { label: 'TOTAL ASSETS', vals: v.totalA, type: 'tot' },
    { label: '', type: 'gap' },
    { label: 'LIABILITIES', type: 'sec' },
    { label: '  Account Payables', vals: v.payTrade, sign: -1 },
    { label: '  Accrued Expenses', vals: v.accrued, sign: -1 },
    { label: '  CURRENT LIABILITIES', vals: v.curLiab, type: 'sub' },
    { label: '  Long Term Liabilities', vals: v.ltl, sign: -1 },
    { label: 'TOTAL LIABILITIES', vals: v.totalL, type: 'tot' },
    { label: '', type: 'gap' },
    { label: 'EQUITY', type: 'sec' },
    { label: '  Capital', vals: v.capital, sign: -1 },
    { label: '  Previous Period Gain/Loss', vals: v.retEarn, sign: -1 },
    { label: '  Gain/Loss Current Period', vals: v.curPeriodGL, sign: 1 },
    { label: 'TOTAL EQUITY', vals: v.totalEq, type: 'tot' },
    { label: '', type: 'gap' },
    { label: 'TOTAL LIABILITIES + EQUITY', vals: v.totalLE, type: 'tot' },
    { label: 'CHECK (A-L-E)', vals: v.check, type: 'chk' },
  ];

  const rows = [];
  rows.push([xc('ECOR SERBIA — BS 12 Months ' + year, { font: { bold: true, sz: 14 } }), ...MSH.map(() => xc('', {}))]);
  rows.push([xc('Kurs: ' + fNum(kurs), { font: { sz: 10 } }), ...MSH.map(() => xc('', {}))]);
  rows.push([xc('', XH), ...MSH.map(m => xc(m, XH))]);

  xlRows.forEach(r => {
    if (r.type === 'gap') { rows.push([xc('', {}), ...MSH.map(() => xc('', {}))]); return; }
    if (r.type === 'sec') { rows.push([xc(r.label, XS), ...MSH.map(() => xc('', XS))]); return; }
    const st = r.type === 'tot' ? XB : r.type === 'sub' ? XSB : r.type === 'chk' ? XT : XN;
    const labelSt = r.type === 'tot' ? XB : r.type === 'sub' ? XSB : r.type === 'chk' ? XT : {};
    const mVals = r.vals ? r.vals.map(val => xc(r.sign ? val * r.sign : val, st)) : MSH.map(() => xc('', st));
    rows.push([xc(r.label, labelSt), ...mVals]);
  });

  const ws = XLSX.utils.aoa_to_sheet(rows.map(r => r.map(c => c.v)));
  ws['!cols'] = [{ wch: 34 }, ...MSH.map(() => ({ wch: 14 }))];
  applyXs(ws, rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'BS 12 Months');
  XLSX.writeFile(wb, 'BS_12Months_' + year + '.xlsx', { bookSST: false, cellStyles: true });
  toast('Excel eksportovan');
  if (state.CU) sb.rpc('log_audit', { p_user_id: state.CU.id, p_action: 'export_excel', p_entity_type: 'mesecni', p_entity_name: 'excel_export' }).then(null, e => console.error('SBIS:', e));
}

// ============================================================
// P&L 12 MONTHS
// ============================================================
let lastPl12 = null;

const PL12_ITEMS = [
  // Revenue
  { key: 'sales', label: 'Sales Revenue (60-61)', test: k => k.startsWith('60') || k.startsWith('61') },
  { key: 'orev', label: 'Other Income (62-64, 66-68)', test: k => /^6[2-8]/.test(k) && !k.startsWith('65') },
  // COGS breakdown
  { key: 'rawMat', label: 'Raw Materials Costs', test: k => k.startsWith('51') && !k.startsWith('512') },
  { key: 'mfg', label: 'Manufacturing Costs', test: k => k.startsWith('50') },
  { key: 'labor', label: 'Direct Labor Costs', test: k => k.startsWith('52') },
  { key: 'energy', label: 'Energy Costs', test: k => k.startsWith('512') },
  { key: 'maint', label: 'Maintenance Costs', test: k => k.startsWith('541') },
  { key: 'wareh', label: 'Warehousing Costs', test: k => k.startsWith('540') },
  // OpEx
  { key: 'dep', label: 'Depreciation (53)', test: k => k.startsWith('53') },
  { key: 'sga', label: 'Selling, General & Admin', test: k => k.startsWith('54') && !k.startsWith('540') && !k.startsWith('541') },
  // Below EBIT
  { key: 'finExp', label: 'Interest Expenses', test: k => k.startsWith('55') },
  { key: 'finInc', label: 'Interest Income', test: k => k.startsWith('65') },
  { key: 'otherNonOp', label: 'Other non-operating', test: k => k.startsWith('56') || k.startsWith('57') || k.startsWith('58') },
];

export async function generatePl12() {
  if (!state.CU) { toast('Niste prijavljeni', 'err'); return; }
  const year = parseInt(document.getElementById('pl12Year').value);
  const kurs = parseFloat(document.getElementById('pl12Kurs').value) || 108;

  showLoading();
  try {
    const promises = [];
    for (let m = 1; m <= 12; m++) {
      promises.push(sb.rpc('calculate_balance_sheet', { p_user_id: state.CU.id, p_year: year, p_month: m, p_kurs: kurs }));
    }
    const results = await Promise.all(promises);
    results.forEach((r, i) => { if (r.error) console.warn('Month ' + (i+1) + ' error:', r.error); });
    const monthsData = results.map(r => r.data || []);
    lastPl12 = { year, kurs, monthsData };
    renderPl12(monthsData, kurs, year);
    document.getElementById('pl12ExpBtn').style.display = '';
    toast('P&L 12 Months generisan za ' + year);
  } catch (e) { toast('Greška: ' + (e.message || e), 'err'); }
  hideLoading();
}

function computePl12(monthsData) {
  const v = {};
  PL12_ITEMS.forEach(it => { v[it.key] = []; for (let m = 0; m < 12; m++) v[it.key].push(calcCat(monthsData[m], it.test)); });
  v.totalRev = []; v.revGrowth = []; v.totalCogs = []; v.gross = []; v.grossPct = [];
  v.totalOpex = []; v.ebit = []; v.ebitPct = [];
  v.interestNet = []; v.net = []; v.netPct = [];
  for (let m = 0; m < 12; m++) {
    // Revenue (class 6 saldo is negative, negate to positive)
    v.totalRev.push(-(v.sales[m] + v.orev[m]));
    v.revGrowth.push(m > 0 && v.totalRev[m - 1] !== 0 ? ((v.totalRev[m] - v.totalRev[m - 1]) / Math.abs(v.totalRev[m - 1])) * 100 : null);
    // COGS (class 5 saldo is positive = debit)
    v.totalCogs.push(v.rawMat[m] + v.mfg[m] + v.labor[m] + v.energy[m] + v.maint[m] + v.wareh[m]);
    // Gross
    v.gross.push(v.totalRev[m] - v.totalCogs[m]);
    v.grossPct.push(v.totalRev[m] !== 0 ? (v.gross[m] / v.totalRev[m]) * 100 : null);
    // OpEx
    v.totalOpex.push(v.dep[m] + v.sga[m]);
    // EBIT
    v.ebit.push(v.gross[m] - v.totalOpex[m]);
    v.ebitPct.push(v.totalRev[m] !== 0 ? (v.ebit[m] / v.totalRev[m]) * 100 : null);
    // Interest net (income is negative saldo from class 6, negate; expense is positive from class 5)
    v.interestNet.push(-v.finInc[m] - v.finExp[m]);
    // Net Income
    v.net.push(v.ebit[m] + v.interestNet[m] - v.otherNonOp[m]);
    v.netPct.push(v.totalRev[m] !== 0 ? (v.net[m] / v.totalRev[m]) * 100 : null);
  }
  return v;
}

function renderPl12(monthsData, kurs, year) {
  const out = document.getElementById('pl12Output'); out.textContent = '';
  const v = computePl12(monthsData);

  const tableRows = [
    { type: 'sec', label: 'REVENUE', color: 'var(--suc)' },
    { type: 'row', label: 'Sales Revenue (60-61)', key: 'sales', sign: -1 },
    { type: 'row', label: 'Other Income (62-64, 66-68)', key: 'orev', sign: -1 },
    { type: 'tot', label: 'Total Revenue', key: 'totalRev', color: 'var(--suc)' },
    { type: 'pct', label: 'M/M Revenue Growth (%)', key: 'revGrowth' },
    { type: 'gap' },
    { type: 'sec', label: 'COST OF GOODS SOLD', color: 'var(--dng)' },
    { type: 'row', label: 'Raw Materials Costs', key: 'rawMat', sign: 1 },
    { type: 'row', label: 'Manufacturing Costs', key: 'mfg', sign: 1 },
    { type: 'row', label: 'Direct Labor Costs', key: 'labor', sign: 1 },
    { type: 'row', label: 'Energy Costs', key: 'energy', sign: 1 },
    { type: 'row', label: 'Maintenance Costs', key: 'maint', sign: 1 },
    { type: 'row', label: 'Warehousing Costs', key: 'wareh', sign: 1 },
    { type: 'tot', label: 'Cost of Goods Sold', key: 'totalCogs', color: 'var(--dng)' },
    { type: 'gap' },
    { type: 'der', label: 'GROSS MARGIN', key: 'gross', color: 'var(--acc)' },
    { type: 'pct', label: 'Gross Margin (%)', key: 'grossPct' },
    { type: 'gap' },
    { type: 'sec', label: 'OPERATING EXPENSES', color: 'var(--dng)' },
    { type: 'row', label: 'Depreciation', key: 'dep', sign: 1 },
    { type: 'row', label: 'Selling, General & Admin', key: 'sga', sign: 1 },
    { type: 'tot', label: 'Total OpEx', key: 'totalOpex', color: 'var(--dng)' },
    { type: 'gap' },
    { type: 'der', label: 'OPERATING INCOME (EBIT)', key: 'ebit', color: null },
    { type: 'pct', label: 'Operating Income Margin (%)', key: 'ebitPct' },
    { type: 'gap' },
    { type: 'row', label: 'Interest Income / (Expenses)', key: 'interestNet', sign: 1 },
    { type: 'row', label: 'Other Non-operating', key: 'otherNonOp', sign: -1 },
    { type: 'gap' },
    { type: 'der', label: 'NET INCOME', key: 'net', color: null },
    { type: 'pct', label: 'Net Income Margin (%)', key: 'netPct' },
  ];

  const card = document.createElement('div'); card.className = 'card'; card.style.cssText = 'padding:0;overflow-x:auto';
  const hdr = document.createElement('div'); hdr.style.cssText = 'padding:16px 20px;border-bottom:2px solid var(--suc)';
  const h2 = document.createElement('h2'); h2.style.cssText = 'font-size:24px;color:var(--suc)'; h2.textContent = 'P&L 12 MONTHS \u2014 ' + year;
  const sub2 = document.createElement('div'); sub2.style.cssText = 'font-size:13px;color:var(--tx3);margin-top:2px'; sub2.textContent = 'ECOR SERBIA \u00B7 Kurs: ' + fNum(kurs);
  hdr.appendChild(h2); hdr.appendChild(sub2); card.appendChild(hdr);

  const tbl = document.createElement('table'); tbl.style.cssText = 'border-collapse:separate;border-spacing:0;table-layout:auto;width:max-content;min-width:100%';
  const stickyBase = 'position:sticky;left:0;z-index:1;min-width:220px;white-space:nowrap;box-shadow:4px 0 8px -2px rgba(0,0,0,0.4)';
  const thead = document.createElement('thead');
  const thr = document.createElement('tr');
  const th0 = document.createElement('th'); th0.style.cssText = 'padding:12px 16px;text-align:left;font-size:13px;color:var(--tx3);background:var(--raised);z-index:2;border-bottom:2px solid var(--border);border-right:2px solid var(--border);' + stickyBase; th0.textContent = '';
  thr.appendChild(th0);
  MSH.forEach((m, i) => { const th = document.createElement('th'); th.textContent = m; th.style.cssText = 'padding:12px 14px;font-size:13px;text-align:right;color:var(--tx3);text-transform:uppercase;letter-spacing:.5px;white-space:nowrap;background:var(--raised);border-bottom:2px solid var(--border)' + (i < 11 ? ';border-right:1px solid var(--border-lt)' : ''); thr.appendChild(th); });
  thead.appendChild(thr); tbl.appendChild(thead);

  const tbody = document.createElement('tbody');
  tableRows.forEach(r => {
    if (r.type === 'gap') {
      const tr = document.createElement('tr');
      const tdS = document.createElement('td'); tdS.style.cssText = 'height:8px;background:var(--raised);border-right:2px solid var(--border);' + stickyBase;
      const tdR = document.createElement('td'); tdR.colSpan = 12; tdR.style.height = '8px';
      tr.appendChild(tdS); tr.appendChild(tdR); tbody.appendChild(tr); return;
    }
    if (r.type === 'sec') {
      const tr = document.createElement('tr');
      const tdS = document.createElement('td'); tdS.style.cssText = 'padding:12px 16px;font-weight:700;font-size:16px;color:' + r.color + ';background:var(--surface);border-right:2px solid var(--border);' + stickyBase;
      tdS.textContent = r.label;
      const tdR = document.createElement('td'); tdR.colSpan = 12; tdR.style.cssText = 'background:var(--surface)';
      tr.appendChild(tdS); tr.appendChild(tdR); tbody.appendChild(tr); return;
    }

    const tr = document.createElement('tr');
    const bg = (r.type === 'tot' || r.type === 'der') ? 'var(--surface)' : 'var(--raised)';
    const tdL = document.createElement('td'); tdL.style.cssText = 'padding:10px 16px;font-size:14px;background:' + bg + ';border-right:2px solid var(--border);border-bottom:1px solid var(--border-lt);' + stickyBase;
    if (r.type === 'row') { tdL.style.paddingLeft = '28px'; tdL.style.color = 'var(--tx)'; }
    if (r.type === 'tot' || r.type === 'der') tdL.style.fontWeight = '700';
    if (r.type === 'tot') tdL.style.color = r.color;
    if (r.type === 'pct') { tdL.style.fontStyle = 'italic'; tdL.style.color = 'var(--tx3)'; }
    tdL.textContent = r.label; tr.appendChild(tdL);

    for (let m = 0; m < 12; m++) {
      const td = document.createElement('td'); td.style.cssText = 'padding:10px 14px;text-align:right;font-family:var(--mn);font-size:14px;white-space:nowrap;border-bottom:1px solid var(--border-lt)' + (m < 11 ? ';border-right:1px solid var(--border-lt)' : '');
      if (r.type === 'pct') {
        const pv = v[r.key][m];
        if (pv !== null) {
          td.textContent = (pv >= 0 ? '+' : '') + pv.toFixed(1) + '%';
          td.style.color = pv >= 0 ? 'var(--suc)' : 'var(--dng)';
        } else { td.textContent = '\u2014'; td.style.color = 'var(--tx3)'; }
        td.style.fontStyle = 'italic'; td.style.fontSize = '13px';
      } else {
        let val = r.type === 'row' ? v[r.key][m] * (r.sign || 1) : v[r.key][m];
        if (r.type === 'tot' || r.type === 'der') { td.style.fontWeight = '700'; td.style.background = 'var(--surface)'; td.style.borderTop = r.type === 'tot' ? '2px solid var(--border)' : 'none'; }
        if (r.type === 'tot') td.style.color = r.color;
        if (r.type === 'der') {
          const c = r.color || (val >= 0 ? 'var(--suc)' : 'var(--dng)');
          td.style.color = c; tdL.style.color = c;
        }
        td.textContent = fNum(val);
      }
      tr.appendChild(td);
    }
    tbody.appendChild(tr);
  });
  tbl.appendChild(tbody); card.appendChild(tbl); out.appendChild(card);
}

export function exportPl12Xlsx() {
  if (!lastPl12) { toast('Generišite P&L 12 Months prvo', 'err'); return; }
  const { year, kurs, monthsData } = lastPl12;
  const v = computePl12(monthsData);

  const xlRows = [
    { label: 'REVENUE', type: 'sec' },
    { label: '  Sales Revenue (60-61)', key: 'sales', sign: -1 },
    { label: '  Other Income (62-64, 66-68)', key: 'orev', sign: -1 },
    { label: 'Total Revenue', key: 'totalRev', type: 'tot' },
    { label: 'M/M Revenue Growth', key: 'revGrowth', type: 'pct' },
    { label: '', type: 'gap' },
    { label: 'COST OF GOODS SOLD', type: 'sec' },
    { label: '  Raw Materials Costs', key: 'rawMat', sign: 1 },
    { label: '  Manufacturing Costs', key: 'mfg', sign: 1 },
    { label: '  Direct Labor Costs', key: 'labor', sign: 1 },
    { label: '  Energy Costs', key: 'energy', sign: 1 },
    { label: '  Maintenance Costs', key: 'maint', sign: 1 },
    { label: '  Warehousing Costs', key: 'wareh', sign: 1 },
    { label: 'Cost of Goods Sold', key: 'totalCogs', type: 'tot' },
    { label: '', type: 'gap' },
    { label: 'GROSS MARGIN', key: 'gross', type: 'tot' },
    { label: 'Gross Margin %', key: 'grossPct', type: 'pct' },
    { label: '', type: 'gap' },
    { label: 'OPERATING EXPENSES', type: 'sec' },
    { label: '  Depreciation', key: 'dep', sign: 1 },
    { label: '  Selling, General & Admin', key: 'sga', sign: 1 },
    { label: 'Total OpEx', key: 'totalOpex', type: 'tot' },
    { label: '', type: 'gap' },
    { label: 'OPERATING INCOME (EBIT)', key: 'ebit', type: 'tot' },
    { label: 'Operating Income Margin %', key: 'ebitPct', type: 'pct' },
    { label: '', type: 'gap' },
    { label: '  Interest Income / (Expenses)', key: 'interestNet', sign: 1 },
    { label: '  Other Non-operating', key: 'otherNonOp', sign: -1 },
    { label: '', type: 'gap' },
    { label: 'NET INCOME', key: 'net', type: 'tot' },
    { label: 'Net Income Margin %', key: 'netPct', type: 'pct' },
  ];

  const rows = [];
  rows.push([xc('ECOR SERBIA — P&L 12 Months ' + year, { font: { bold: true, sz: 14 } }), ...MSH.map(() => xc('', {}))]);
  rows.push([xc('Kurs: ' + fNum(kurs), { font: { sz: 10 } }), ...MSH.map(() => xc('', {}))]);
  rows.push([xc('', XH), ...MSH.map(m => xc(m, XH))]);

  xlRows.forEach(r => {
    if (r.type === 'gap') { rows.push([xc('', {}), ...MSH.map(() => xc('', {}))]); return; }
    if (r.type === 'sec') { rows.push([xc(r.label, XS), ...MSH.map(() => xc('', XS))]); return; }
    const st = r.type === 'tot' ? XB : XN;
    if (r.type === 'pct') {
      const mVals = v[r.key].map(pv => xc(pv !== null ? pv / 100 : null, { font: { sz: 10, italic: true }, numFmt: '0.0%' }));
      rows.push([xc(r.label, { font: { italic: true } }), ...mVals]);
    } else {
      const mVals = v[r.key].map(val => xc(r.sign ? val * r.sign : val, st));
      rows.push([xc(r.label, r.type === 'tot' ? XB : {}), ...mVals]);
    }
  });

  const ws = XLSX.utils.aoa_to_sheet(rows.map(r => r.map(c => c.v)));
  ws['!cols'] = [{ wch: 28 }, ...MSH.map(() => ({ wch: 14 }))];
  applyXs(ws, rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'P&L 12 Months');
  XLSX.writeFile(wb, 'PL_12Months_' + year + '.xlsx', { bookSST: false, cellStyles: true });
  toast('Excel eksportovan');
  if (state.CU) sb.rpc('log_audit', { p_user_id: state.CU.id, p_action: 'export_excel', p_entity_type: 'mesecni', p_entity_name: 'excel_export' }).then(null, e => console.error('SBIS:', e));
}

// ============================================================
// INVENTORY 12 MONTHS
// ============================================================
let lastInv12 = null;

const INV12_CATS = [
  { key: 'mat', label: 'Materijal (101)', test: k => k.startsWith('101') && !k.startsWith('1012') && !k.startsWith('1013') },
  { key: 'waste', label: 'Otpadni materijal (1012)', test: k => k.startsWith('1012') },
  { key: 'fuel', label: 'Gorivo i mazivo (1013)', test: k => k.startsWith('1013') },
  { key: 'spare', label: 'Rezervni delovi (102)', test: k => k.startsWith('102') },
  { key: 'tools', label: 'Alat i sitan inventar (103)', test: k => k.startsWith('103') },
  { key: 'wip', label: 'Nedovršena proizvodnja (110)', test: k => k.startsWith('110') || k.startsWith('111') },
  { key: 'finished', label: 'Gotovi proizvodi (12)', test: k => k.startsWith('12') },
  { key: 'goods', label: 'Roba (13)', test: k => k.startsWith('13') },
  { key: 'advances', label: 'Plaćeni avansi (15)', test: k => k.startsWith('15') },
  { key: 'other', label: 'Ostalo (1xx)', test: k => k.startsWith('1') && !k.startsWith('101') && !k.startsWith('102') && !k.startsWith('103') && !k.startsWith('110') && !k.startsWith('111') && !k.startsWith('12') && !k.startsWith('13') && !k.startsWith('15') && !k.startsWith('1012') && !k.startsWith('1013') },
];

function computeInv12(monthsData) {
  const v = {};
  INV12_CATS.forEach(it => { v[it.key] = []; for (let m = 0; m < 12; m++) v[it.key].push(calcCat(monthsData[m], it.test)); });
  v.total = [];
  for (let m = 0; m < 12; m++) {
    v.total.push(INV12_CATS.reduce((s, it) => s + v[it.key][m], 0));
  }
  return v;
}

export async function generateInv12() {
  if (!state.CU) { toast('Niste prijavljeni', 'err'); return; }
  const year = parseInt(document.getElementById('inv12Year').value);
  const kurs = parseFloat(document.getElementById('inv12Kurs').value) || 108;

  showLoading();
  try {
    const promises = [];
    for (let m = 1; m <= 12; m++) {
      promises.push(sb.rpc('calculate_balance_sheet', { p_user_id: state.CU.id, p_year: year, p_month: m, p_kurs: kurs }));
    }
    const results = await Promise.all(promises);
    results.forEach((r, i) => { if (r.error) console.warn('Month ' + (i+1) + ' error:', r.error); });
    const monthsData = results.map(r => r.data || []);
    lastInv12 = { year, kurs, monthsData };
    renderInv12(monthsData, kurs, year);
    document.getElementById('inv12ExpBtn').style.display = '';
    toast('Inventory 12 Months generisan za ' + year);
  } catch (e) { toast('Greška: ' + (e.message || e), 'err'); }
  hideLoading();
}

function renderInv12(monthsData, kurs, year) {
  const out = document.getElementById('inv12Output'); out.textContent = '';
  const v = computeInv12(monthsData);

  const tableRows = [
    ...INV12_CATS.map(it => ({ type: 'row', label: it.label, key: it.key })),
    { type: 'gap' },
    { type: 'tot', label: 'TOTAL INVENTORY', key: 'total', color: 'var(--acc)' },
  ];

  const card = document.createElement('div'); card.className = 'card'; card.style.cssText = 'padding:0;overflow-x:auto';
  const hdr = document.createElement('div'); hdr.style.cssText = 'padding:16px 20px;border-bottom:2px solid var(--acc)';
  const h2 = document.createElement('h2'); h2.style.cssText = 'font-size:24px;color:var(--acc)'; h2.textContent = 'INVENTORY 12 MONTHS \u2014 ' + year;
  const sub2 = document.createElement('div'); sub2.style.cssText = 'font-size:13px;color:var(--tx3);margin-top:2px'; sub2.textContent = 'ECOR SERBIA \u00B7 Kurs: ' + fNum(kurs);
  hdr.appendChild(h2); hdr.appendChild(sub2); card.appendChild(hdr);

  const tbl = document.createElement('table'); tbl.style.cssText = 'border-collapse:separate;border-spacing:0;table-layout:auto;width:max-content;min-width:100%';
  const stickyBase = 'position:sticky;left:0;z-index:1;min-width:220px;white-space:nowrap;box-shadow:4px 0 8px -2px rgba(0,0,0,0.4)';
  const thead = document.createElement('thead');
  const thr = document.createElement('tr');
  const th0 = document.createElement('th'); th0.style.cssText = 'padding:12px 16px;text-align:left;font-size:13px;color:var(--tx3);background:var(--raised);z-index:2;border-bottom:2px solid var(--border);border-right:2px solid var(--border);' + stickyBase; th0.textContent = '';
  thr.appendChild(th0);
  MSH.forEach((m, i) => { const th = document.createElement('th'); th.textContent = m; th.style.cssText = 'padding:12px 14px;font-size:13px;text-align:right;color:var(--tx3);text-transform:uppercase;letter-spacing:.5px;white-space:nowrap;background:var(--raised);border-bottom:2px solid var(--border)' + (i < 11 ? ';border-right:1px solid var(--border-lt)' : ''); thr.appendChild(th); });
  thead.appendChild(thr); tbl.appendChild(thead);

  const tbody = document.createElement('tbody');
  tableRows.forEach(r => {
    if (r.type === 'gap') {
      const tr = document.createElement('tr');
      const tdS = document.createElement('td'); tdS.style.cssText = 'height:8px;background:var(--raised);border-right:2px solid var(--border);' + stickyBase;
      const tdR = document.createElement('td'); tdR.colSpan = 12; tdR.style.height = '8px';
      tr.appendChild(tdS); tr.appendChild(tdR); tbody.appendChild(tr); return;
    }
    const tr = document.createElement('tr');
    const isTot = r.type === 'tot';
    const bg = isTot ? 'var(--surface)' : 'var(--raised)';
    const tdL = document.createElement('td'); tdL.style.cssText = 'padding:10px 16px;font-size:14px;background:' + bg + ';border-right:2px solid var(--border);border-bottom:1px solid var(--border-lt);' + stickyBase;
    if (r.type === 'row') { tdL.style.paddingLeft = '16px'; tdL.style.color = 'var(--tx)'; }
    if (isTot) { tdL.style.fontWeight = '700'; tdL.style.color = r.color; }
    tdL.textContent = r.label; tr.appendChild(tdL);

    for (let m = 0; m < 12; m++) {
      const td = document.createElement('td'); td.style.cssText = 'padding:10px 14px;text-align:right;font-family:var(--mn);font-size:14px;white-space:nowrap;border-bottom:1px solid var(--border-lt)' + (m < 11 ? ';border-right:1px solid var(--border-lt)' : '');
      const val = v[r.key][m];
      if (isTot) { td.style.fontWeight = '700'; td.style.background = 'var(--surface)'; td.style.borderTop = '2px solid var(--border)'; td.style.color = r.color; }
      td.textContent = fNum(val);
      tr.appendChild(td);
    }
    tbody.appendChild(tr);
  });
  tbl.appendChild(tbody); card.appendChild(tbl); out.appendChild(card);
}

export function exportInv12Xlsx() {
  if (!lastInv12) { toast('Generišite Inventory 12 Months prvo', 'err'); return; }
  const { year, kurs, monthsData } = lastInv12;
  const v = computeInv12(monthsData);

  const xlRows = [
    ...INV12_CATS.map(it => ({ label: '  ' + it.label, key: it.key })),
    { label: '', type: 'gap' },
    { label: 'TOTAL INVENTORY', key: 'total', type: 'tot' },
  ];

  const rows = [];
  rows.push([xc('ECOR SERBIA — Inventory 12 Months ' + year, { font: { bold: true, sz: 14 } }), ...MSH.map(() => xc('', {}))]);
  rows.push([xc('Kurs: ' + fNum(kurs), { font: { sz: 10 } }), ...MSH.map(() => xc('', {}))]);
  rows.push([xc('', XH), ...MSH.map(m => xc(m, XH))]);

  xlRows.forEach(r => {
    if (r.type === 'gap') { rows.push([xc('', {}), ...MSH.map(() => xc('', {}))]); return; }
    const st = r.type === 'tot' ? XB : XN;
    const mVals = v[r.key].map(val => xc(val, st));
    rows.push([xc(r.label, r.type === 'tot' ? XB : {}), ...mVals]);
  });

  const ws = XLSX.utils.aoa_to_sheet(rows.map(r => r.map(c => c.v)));
  ws['!cols'] = [{ wch: 30 }, ...MSH.map(() => ({ wch: 14 }))];
  applyXs(ws, rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Inventory 12 Months');
  XLSX.writeFile(wb, 'Inventory_12Months_' + year + '.xlsx', { bookSST: false, cellStyles: true });
  toast('Excel eksportovan');
  if (state.CU) sb.rpc('log_audit', { p_user_id: state.CU.id, p_action: 'export_excel', p_entity_type: 'mesecni', p_entity_name: 'excel_export' }).then(null, e => console.error('SBIS:', e));
}
